import { useCallback, useEffect, useRef, useState } from 'react'
import { AudioEngine } from './audio/engine'
import { renderOffline } from './audio/offlineRenderer'
import { encodeAudio, formatExtension, type AudioFormat, type BitDepth } from './utils/wavEncoder'
import type { LoopState, RoutingMode, LoadedFile } from './audio/types'
import { WaveformView } from './components/WaveformView'
import { Fader } from './components/Fader'
import { Knob } from './components/Knob'
import { EQCurve } from './components/EQCurve'
import { VUMeter } from './components/VUMeter'
import { Limiter } from './components/Limiter'
import { MultibandView } from './components/MultibandView'
import { FileList } from './components/FileList'
import './styles/app.css'

const DEFAULT_BPM = 120
const DEFAULT_STEPS = 32

/** Only skip app shortcuts when the user is typing text (Space inserts a character) */
function isTextInputElement(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false
  const tag = el.tagName
  if (tag === 'TEXTAREA') return true
  if (tag === 'SELECT') return true
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type?.toLowerCase() || 'text'
    const textTypes = ['text', 'search', 'email', 'url', 'tel', 'password', 'number']
    return textTypes.includes(type)
  }
  if (el.isContentEditable) return true
  return false
}

function App() {
  const engineRef = useRef(new AudioEngine(() => setIsPlaying(false)))
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const dspLeftRef = useRef<HTMLDivElement | null>(null)

  const [loopState, setLoopState] = useState<LoopState>({
    buffer: null,
    clips: [],
    bpm: DEFAULT_BPM,
    steps: DEFAULT_STEPS,
  })
  const [files, setFiles] = useState<LoadedFile[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [isPlaying, setIsPlaying] = useState(false)
  const [_status, setStatus] = useState<string | null>(null)
  type ModuleId = 'eq' | 'multiband' | 'compressor' | 'overdrive' | 'transient' | 'delay' | 'rsreverb' | 'reverb' | 'limiter'
  const [activeModule, setActiveModule] = useState<ModuleId>('eq')
  const [moduleEnabled, setModuleEnabled] = useState({
    eq: true,
    multiband: false,
    compressor: false,
    overdrive: false,
    transient: false,
    delay: false,
    rsreverb: false,
    reverb: false,
    limiter: false,
  })
  const [mixState, setMixState] = useState({
    eq: {
      dryDb: -6,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: true,
      routingMode: 'parallel' as RoutingMode,
    },
    multiband: {
      dryDb: -6,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: true,
      routingMode: 'parallel' as RoutingMode,
    },
    compressor: {
      dryDb: -6,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: true,
      routingMode: 'parallel' as RoutingMode,
    },
    overdrive: {
      dryDb: -6,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: true,
      routingMode: 'parallel' as RoutingMode,
    },
    transient: {
      dryDb: -6,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: true,
      routingMode: 'parallel' as RoutingMode,
    },
    delay: {
      dryDb: 0,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: false,
      routingMode: 'parallel' as RoutingMode,
    },
    rsreverb: {
      dryDb: 0,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: false,
      routingMode: 'parallel' as RoutingMode,
    },
    reverb: {
      dryDb: 0,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: false,
      routingMode: 'parallel' as RoutingMode,
    },
    limiter: {
      dryDb: -6,
      wetDb: -6,
      drySolo: false,
      wetSolo: false,
      gangEnabled: false,
      inverseEnabled: true,
      routingMode: 'parallel' as RoutingMode,
    },
  })
  const [masterDb, setMasterDb] = useState(0)
  const [lowShelfDb, setLowShelfDb] = useState(0)
  const [mid1Db, setMid1Db] = useState(0)
  const [mid2Db, setMid2Db] = useState(0)
  const [highShelfDb, setHighShelfDb] = useState(0)
  const [lowShelfFreq, setLowShelfFreq] = useState(80)
  const [mid1Freq, setMid1Freq] = useState(700)
  const [mid2Freq, setMid2Freq] = useState(3000)
  const [highShelfFreq, setHighShelfFreq] = useState(8000)
  const [mid1Q, setMid1Q] = useState(0.5)
  const [mid2Q, setMid2Q] = useState(0.5)
  const [heatAmount, setHeatAmount] = useState(0)
  const [mbLowMidFreq, setMbLowMidFreq] = useState(200)
  const [mbMidHighFreq, setMbMidHighFreq] = useState(4000)
  const [mbLowGain, setMbLowGain] = useState(0)
  const [mbMidGain, setMbMidGain] = useState(0)
  const [mbHighGain, setMbHighGain] = useState(0)
  const [mbLowDrive, setMbLowDrive] = useState(0)
  const [mbMidDrive, setMbMidDrive] = useState(0)
  const [mbHighDrive, setMbHighDrive] = useState(0)
  const [mbLowType, setMbLowType] = useState('tube-warm')
  const [mbMidType, setMbMidType] = useState('tube-warm')
  const [mbHighType, setMbHighType] = useState('tube-warm')
  const [mbGlobalDrive, setMbGlobalDrive] = useState(1)
  const [compType, setCompType] = useState<'peak' | 'rms' | 'peak-dynamics-lo' | 'rms-dynamics-lo'>('peak')
  const [compThreshold, setCompThreshold] = useState(0)
  const [compRatio, setCompRatio] = useState(2)
  const [compAttack, setCompAttack] = useState(30)
  const [compRelease, setCompRelease] = useState(200)
  const [compMakeup, setCompMakeup] = useState(0)
  const [compKnee, setCompKnee] = useState(0.4)
  const [compDistAmount, setCompDistAmount] = useState(0)
  const [compDistMode, setCompDistMode] = useState<'valve' | 'solid'>('valve')
  const [compDistPosition, setCompDistPosition] = useState<'pre' | 'post'>('post')
  const [odDriveType, setOdDriveType] = useState('tube-warm')
  const [odDriveAmount, setOdDriveAmount] = useState(0)
  const [odTightHz, setOdTightHz] = useState(80)
  const [odToneHz, setOdToneHz] = useState(8000)
  const [odBias, setOdBias] = useState(0)
  const [odOutputDb, setOdOutputDb] = useState(0)
  const [transAttackGain, setTransAttackGain] = useState(0)
  const [transSustainGain, setTransSustainGain] = useState(0)
  const [transHoldTime, setTransHoldTime] = useState(0)
  const [transDecay, setTransDecay] = useState(30)
  const [transOutputDb, setTransOutputDb] = useState(0)
  const [delayMs, setDelayMs] = useState(350)
  const [delayFeedback, setDelayFeedback] = useState(0.35)
  const [delayMix, setDelayMix] = useState(1)
  const [delayMode, setDelayMode] = useState<'stereo' | 'pingpong'>('stereo')
  const [delaySyncEnabled, setDelaySyncEnabled] = useState(false)
  const [delayBpm, setDelayBpm] = useState(120)
  const [delayDivision, setDelayDivision] = useState('1/8')
  const [delayTone, setDelayTone] = useState(0.7)
  const [delaySaturation, setDelaySaturation] = useState(0.15)
  const [delayWidth, setDelayWidth] = useState(1)
  const [revRoomType, setRevRoomType] = useState('medium-room')
  const [revPredelay, setRevPredelay] = useState(25)
  const [revSize, setRevSize] = useState(0.5)
  const [revDecay, setRevDecay] = useState(0.5)
  const [revDamping, setRevDamping] = useState(0.6)
  const [revBandwidth, setRevBandwidth] = useState(0.75)
  const [revDensity, setRevDensity] = useState(0.6)
  const [revEarlyLate, setRevEarlyLate] = useState(0.5)
  const [revModRate, setRevModRate] = useState(0.5)
  const [revModDepth, setRevModDepth] = useState(0.7)
  const [revOutputDb, setRevOutputDb] = useState(0)
  const [rsRoomSize, setRsRoomSize] = useState(0.71)
  const [rsPreDelay, setRsPreDelay] = useState(8)
  const [rsFeedback, setRsFeedback] = useState(0.48)
  const [rsDamping, setRsDamping] = useState(0.72)
  const [rsDiffusion, setRsDiffusion] = useState(0.45)
  const [rsEarlySize, setRsEarlySize] = useState(0.84)
  const [rsEarlyDecay, setRsEarlyDecay] = useState(0.23)
  const [rsModRate, setRsModRate] = useState(0.72)
  const [rsModDepth, setRsModDepth] = useState(0.79)
  const [rsHighCut, setRsHighCut] = useState(7620)
  const [rsLowCut, setRsLowCut] = useState(65)
  const [rsEarlyLateMix, setRsEarlyLateMix] = useState(0.54)
  const [rsOutputDb, setRsOutputDb] = useState(0)
  const [limThreshold, setLimThreshold] = useState(0)
  const [limCeiling, setLimCeiling] = useState(0)
  const [limRelease, setLimRelease] = useState(100)
  const [limKnee, setLimKnee] = useState(0.4)
  const [limMode, setLimMode] = useState<'pumping' | 'normal' | 'smooth'>('normal')
  const [fileName, setFileName] = useState<string>('')
  const [limiterEnabled, setLimiterEnabled] = useState(true)
  const [gainReduction, setGainReduction] = useState(0)
  const [compReductionDb, setCompReductionDb] = useState(0)
  const [limReductionDb, setLimReductionDb] = useState(0)
  const [masterBypassed, setMasterBypassed] = useState(false)
  const [peakDbLeft, setPeakDbLeft] = useState(-96)
  const [peakDbRight, setPeakDbRight] = useState(-96)
  const [playheadTime, setPlayheadTime] = useState<number | null>(null)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isManualOpen, setIsManualOpen] = useState(false)
  const [finalPeakDb, setFinalPeakDb] = useState(-0.2)
  const [audioReady, setAudioReady] = useState(false)

  // Export settings
  const [exportFormat, setExportFormat] = useState<AudioFormat>('wav')
  const [exportBitDepth, setExportBitDepth] = useState<BitDepth>(24)
  const [isRendering, setIsRendering] = useState(false)
  const [dspLeftHeight, setDspLeftHeight] = useState(0)
  const [extendReverbTail, setExtendReverbTail] = useState(false)
  const [reverbTailThreshold, setReverbTailThreshold] = useState(-75)

  const MASTER_MIN_DB = -48
  const MASTER_MAX_DB = 12
  const FINAL_PEAK_MIN_DB = -16
  const FINAL_PEAK_MAX_DB = -0.2
  const MIX_MIN_DB = -80
  const MIX_MAX_DB = 6
  const REVERB_TAIL_THRESHOLD_MIN_DB = -120
  const REVERB_TAIL_THRESHOLD_MAX_DB = -40

  const applyModuleMixGains = useCallback(
    (moduleId: ModuleId, nextDryDb: number, nextWetDb: number, nextDrySolo: boolean, nextWetSolo: boolean) => {
      const moduleState = mixState[moduleId]
      if (moduleState.routingMode === 'serial') {
        const dry = Number.NEGATIVE_INFINITY
        const wet = 0
        if (moduleId === 'eq') {
          engineRef.current.setEqDryDb(dry)
          engineRef.current.setEqWetDb(wet)
        } else if (moduleId === 'multiband') {
          engineRef.current.setMultibandDryDb(dry)
          engineRef.current.setMultibandWetDb(wet)
        } else if (moduleId === 'compressor') {
          engineRef.current.setCompDryDb(dry)
          engineRef.current.setCompWetDb(wet)
        } else if (moduleId === 'overdrive') {
          engineRef.current.setOverdriveDryDb(dry)
          engineRef.current.setOverdriveWetDb(wet)
        } else if (moduleId === 'transient') {
          engineRef.current.setTransientDryDb(dry)
          engineRef.current.setTransientWetDb(wet)
        } else if (moduleId === 'delay') {
          engineRef.current.setDelayDryDb(dry)
          engineRef.current.setDelayWetDb(wet)
        } else if (moduleId === 'rsreverb') {
          engineRef.current.setRsReverbDryDb(dry)
          engineRef.current.setRsReverbWetDb(wet)
        } else if (moduleId === 'reverb') {
          engineRef.current.setReverbDryDb(dry)
          engineRef.current.setReverbWetDb(wet)
        } else {
          engineRef.current.setLimiterDryDb(dry)
          engineRef.current.setLimiterWetDb(wet)
        }
        return
      }

      let dry = nextDryDb
      let wet = nextWetDb
      if (nextDrySolo && !nextWetSolo) {
        wet = Number.NEGATIVE_INFINITY
      } else if (nextWetSolo && !nextDrySolo) {
        dry = Number.NEGATIVE_INFINITY
      }

      if (moduleId === 'eq') {
        engineRef.current.setEqDryDb(dry)
        engineRef.current.setEqWetDb(wet)
      } else if (moduleId === 'multiband') {
        engineRef.current.setMultibandDryDb(dry)
        engineRef.current.setMultibandWetDb(wet)
      } else if (moduleId === 'compressor') {
        engineRef.current.setCompDryDb(dry)
        engineRef.current.setCompWetDb(wet)
      } else if (moduleId === 'overdrive') {
        engineRef.current.setOverdriveDryDb(dry)
        engineRef.current.setOverdriveWetDb(wet)
      } else if (moduleId === 'transient') {
        engineRef.current.setTransientDryDb(dry)
        engineRef.current.setTransientWetDb(wet)
      } else if (moduleId === 'delay') {
        engineRef.current.setDelayDryDb(dry)
        engineRef.current.setDelayWetDb(wet)
      } else if (moduleId === 'rsreverb') {
        engineRef.current.setRsReverbDryDb(dry)
        engineRef.current.setRsReverbWetDb(wet)
      } else if (moduleId === 'reverb') {
        engineRef.current.setReverbDryDb(dry)
        engineRef.current.setReverbWetDb(wet)
      } else {
        engineRef.current.setLimiterDryDb(dry)
        engineRef.current.setLimiterWetDb(wet)
      }
    },
    [mixState],
  )

  const toggleTransport = useCallback(async () => {
    if (!loopState.buffer) {
      setStatus('Load a loop to start playback.')
      return
    }

    if (isPlaying) {
      engineRef.current.stop()
      setIsPlaying(false)
      return
    }

    try {
      engineRef.current.setRoutingMode(mixState.eq.routingMode)
      engineRef.current.setMasterDb(masterDb)
      applyModuleMixGains('eq', mixState.eq.dryDb, mixState.eq.wetDb, mixState.eq.drySolo, mixState.eq.wetSolo)
      applyModuleMixGains(
        'multiband',
        mixState.multiband.dryDb,
        mixState.multiband.wetDb,
        mixState.multiband.drySolo,
        mixState.multiband.wetSolo,
      )
      applyModuleMixGains('compressor', mixState.compressor.dryDb, mixState.compressor.wetDb, mixState.compressor.drySolo, mixState.compressor.wetSolo)
      applyModuleMixGains('overdrive', mixState.overdrive.dryDb, mixState.overdrive.wetDb, mixState.overdrive.drySolo, mixState.overdrive.wetSolo)
      applyModuleMixGains('transient', mixState.transient.dryDb, mixState.transient.wetDb, mixState.transient.drySolo, mixState.transient.wetSolo)
      applyModuleMixGains('delay', mixState.delay.dryDb, mixState.delay.wetDb, mixState.delay.drySolo, mixState.delay.wetSolo)
      applyModuleMixGains('rsreverb', mixState.rsreverb.dryDb, mixState.rsreverb.wetDb, mixState.rsreverb.drySolo, mixState.rsreverb.wetSolo)
      applyModuleMixGains('reverb', mixState.reverb.dryDb, mixState.reverb.wetDb, mixState.reverb.drySolo, mixState.reverb.wetSolo)
      applyModuleMixGains('limiter', mixState.limiter.dryDb, mixState.limiter.wetDb, mixState.limiter.drySolo, mixState.limiter.wetSolo)
      engineRef.current.setEqEnabled(moduleEnabled.eq)
      engineRef.current.setMultibandEnabled(moduleEnabled.multiband)
      engineRef.current.setCompEnabled(moduleEnabled.compressor)
      engineRef.current.setOverdriveEnabled(moduleEnabled.overdrive)
      engineRef.current.setTransientEnabled(moduleEnabled.transient)
      engineRef.current.setDelayEnabled(moduleEnabled.delay)
      engineRef.current.setRsReverbEnabled(moduleEnabled.rsreverb)
      engineRef.current.setReverbEnabled(moduleEnabled.reverb)
      engineRef.current.setLimiterModuleEnabled(moduleEnabled.limiter)
      await engineRef.current.play(loopState.buffer, mixState.eq.routingMode)
      setIsPlaying(true)
      setStatus(null)
    } catch (err) {
      console.error(err)
      setStatus('Failed to start playback (check browser audio permissions).')
    }
  }, [applyModuleMixGains, isPlaying, loopState.buffer, masterDb, mixState, moduleEnabled])

  // Shared helper: snapshot → render → encode → download for one buffer
  const renderAndDownload = useCallback(async (
    buffer: AudioBuffer,
    sourceName: string,
  ) => {
    // Ensure engine reflects current UI state for both reverbs before snapshot
    const renderEngine = engineRef.current as AudioEngine & {
      setOutputTrimDb?: (db: number) => void
      setMasterLimiterThreshold?: (db: number) => void
    }
    renderEngine.setMasterDb(masterDb)
    renderEngine.setBypass(masterBypassed)
    renderEngine.setMasterLimiterEnabled(limiterEnabled)
    if (typeof renderEngine.setOutputTrimDb === 'function') {
      renderEngine.setOutputTrimDb(finalPeakDb)
    } else if (typeof renderEngine.setMasterLimiterThreshold === 'function') {
      renderEngine.setMasterLimiterThreshold(finalPeakDb)
    }

    engineRef.current.setDelayEnabled(moduleEnabled.delay)
    engineRef.current.setRsReverbEnabled(moduleEnabled.rsreverb)
    engineRef.current.setReverbEnabled(moduleEnabled.reverb)
    applyModuleMixGains('delay', mixState.delay.dryDb, mixState.delay.wetDb, mixState.delay.drySolo, mixState.delay.wetSolo)
    engineRef.current.setDelayTimeMs(delayMs)
    engineRef.current.setDelayFeedback(delayFeedback)
    engineRef.current.setDelayMix(delayMix)
    engineRef.current.setDelayMode(delayMode)
    engineRef.current.setDelaySync(delaySyncEnabled)
    engineRef.current.setDelayBpm(delayBpm)
    engineRef.current.setDelayDivision(delayDivision)
    engineRef.current.setDelayTone(delayTone)
    engineRef.current.setDelaySaturation(delaySaturation)
    engineRef.current.setDelayWidth(delayWidth)
    applyModuleMixGains('rsreverb', mixState.rsreverb.dryDb, mixState.rsreverb.wetDb, mixState.rsreverb.drySolo, mixState.rsreverb.wetSolo)
    applyModuleMixGains('reverb', mixState.reverb.dryDb, mixState.reverb.wetDb, mixState.reverb.drySolo, mixState.reverb.wetSolo)
    engineRef.current.setRsRoomSize(rsRoomSize)
    engineRef.current.setRsPreDelay(rsPreDelay)
    engineRef.current.setRsFeedback(rsFeedback)
    engineRef.current.setRsDamping(rsDamping)
    engineRef.current.setRsDiffusion(rsDiffusion)
    engineRef.current.setRsEarlySize(rsEarlySize)
    engineRef.current.setRsEarlyDecay(rsEarlyDecay)
    engineRef.current.setRsModRate(rsModRate)
    engineRef.current.setRsModDepth(rsModDepth)
    engineRef.current.setRsHighCut(rsHighCut)
    engineRef.current.setRsLowCut(rsLowCut)
    engineRef.current.setRsEarlyLateMix(rsEarlyLateMix)
    engineRef.current.setRsOutputDb(rsOutputDb)
    engineRef.current.setReverbRoomType(revRoomType)
    engineRef.current.setReverbPredelay(revPredelay)
    engineRef.current.setReverbSize(revSize)
    engineRef.current.setReverbDecay(revDecay)
    engineRef.current.setReverbDamping(revDamping)
    engineRef.current.setReverbBandwidth(revBandwidth)
    engineRef.current.setReverbDensity(revDensity)
    engineRef.current.setReverbEarlyLate(revEarlyLate)
    engineRef.current.setReverbModRate(revModRate)
    engineRef.current.setReverbModDepth(revModDepth)
    engineRef.current.setReverbOutputDb(revOutputDb)

    // Fresh snapshot every time — mirrors exactly what single render does
    const state = engineRef.current.getStateSnapshot()
    const rsDelta = Math.max(
      Math.abs(state.rsRoomSize - rsRoomSize),
      Math.abs(state.rsPreDelayMs - rsPreDelay),
      Math.abs(state.rsFeedback - rsFeedback),
      Math.abs(state.rsEarlyLateMix - rsEarlyLateMix),
    )
    const revDelta = Math.max(
      Math.abs(state.revPredelayMs - revPredelay),
      Math.abs(state.revDecay - revDecay),
      Math.abs(state.revEarlyLate - revEarlyLate),
      Math.abs(state.revModRate - revModRate),
      Math.abs(state.revModDepth - revModDepth),
    )
    if (rsDelta > 1e-3 || revDelta > 1e-3) {
      console.warn('[Render Sync] Snapshot mismatch detected', { rsDelta, revDelta, state })
    }
    const rendered = await renderOffline(buffer, state, {
      extendReverbTail,
      reverbTailThreshold,
    })
    if (extendReverbTail) {
      const inputSeconds = buffer.length / buffer.sampleRate
      const outputSeconds = rendered.length / rendered.sampleRate
      const tailSeconds = Math.max(0, outputSeconds - inputSeconds)
      console.info(
        '[Render]',
        `input=${inputSeconds.toFixed(3)}s`,
        `output=${outputSeconds.toFixed(3)}s`,
        `tail=${tailSeconds.toFixed(3)}s`,
        `threshold=${reverbTailThreshold}dBFS`,
      )
    }

    // Extract channel data
    const channels: Float32Array[] = []
    for (let ch = 0; ch < rendered.numberOfChannels; ch++) {
      channels.push(rendered.getChannelData(ch))
    }

    // Encode to selected format
    const blob = encodeAudio({
      format: exportFormat,
      bitDepth: exportBitDepth,
      sampleRate: rendered.sampleRate,
      channels,
    })

    // Generate filename
    const baseName = sourceName
      ? sourceName.replace(/\.[^.]+$/, '')
      : 'rendered-loop'
    const ext = formatExtension(exportFormat)
    const downloadName = `${baseName}_processed.${ext}`

    // Trigger download
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = downloadName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [
    applyModuleMixGains,
    exportFormat,
    exportBitDepth,
    extendReverbTail,
    reverbTailThreshold,
    masterDb,
    masterBypassed,
    limiterEnabled,
    finalPeakDb,
    moduleEnabled.delay,
    moduleEnabled.rsreverb,
    moduleEnabled.reverb,
    mixState.delay,
    mixState.rsreverb,
    mixState.reverb,
    delayMs,
    delayFeedback,
    delayMix,
    delayMode,
    delaySyncEnabled,
    delayBpm,
    delayDivision,
    delayTone,
    delaySaturation,
    delayWidth,
    rsRoomSize,
    rsPreDelay,
    rsFeedback,
    rsDamping,
    rsDiffusion,
    rsEarlySize,
    rsEarlyDecay,
    rsModRate,
    rsModDepth,
    rsHighCut,
    rsLowCut,
    rsEarlyLateMix,
    rsOutputDb,
    revRoomType,
    revPredelay,
    revSize,
    revDecay,
    revDamping,
    revBandwidth,
    revDensity,
    revEarlyLate,
    revModRate,
    revModDepth,
    revOutputDb,
  ])

  const handleRender = useCallback(async () => {
    if (!loopState.buffer || isRendering) return
    setIsRendering(true)
    try {
      await renderAndDownload(loopState.buffer, fileName)
    } catch (err) {
      console.error('Render failed:', err)
    } finally {
      setIsRendering(false)
    }
  }, [loopState.buffer, isRendering, fileName, renderAndDownload])

  useEffect(() => {
    let mounted = true
    engineRef.current.getContext().then(ctx => {
      if (!mounted) return
      const update = () => {
        if (mounted) setAudioReady(ctx.state === 'running')
      }
      update()
      ctx.onstatechange = update
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {})
      }
    })
    return () => {
      mounted = false
      engineRef.current.stop()
    }
  }, [])

  // Refactored handleSelectFile to be used by UI
  const selectFile = useCallback((index: number, fileList: LoadedFile[] = files) => {
    if (index < 0 || index >= fileList.length) return
    
    // ALWAYS stop playback before switching samples
    engineRef.current.stop()
    setIsPlaying(false)
    
    const selected = fileList[index]
    setSelectedIndex(index)
    setLoopState({ 
      buffer: selected.buffer, 
      clips: [], 
      bpm: DEFAULT_BPM, 
      steps: DEFAULT_STEPS 
    })
    setFileName(selected.name)
  }, [files])

  // Improved handleFileSelect that works with state
  const processFiles = async (incomingFiles: FileList | File[]) => {
    setStatus('Decoding…')
    const processed: LoadedFile[] = []
    const rawFiles = Array.isArray(incomingFiles) ? incomingFiles : Array.from(incomingFiles)

    for (const file of rawFiles) {
      try {
        const result = await engineRef.current.loadLoop(file, { totalSteps: DEFAULT_STEPS })
        processed.push({
          file,
          buffer: result.buffer,
          name: file.name,
          duration: result.buffer.duration,
          sampleRate: result.buffer.sampleRate,
          bitDepth: 24 
        })
      } catch (e) {
        console.error(e)
      }
    }

    if (processed.length > 0) {
      setFiles(prev => [...prev, ...processed])
      setStatus(`Added ${processed.length} files`)
    } else {
      setStatus('No audio files loaded')
    }
  }

  // Auto-select first file if list was empty
  useEffect(() => {
    if (files.length > 0 && selectedIndex === -1) {
      selectFile(0)
    }
  }, [files, selectedIndex, selectFile])

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files)
    }
  }
  
  const handleExportAll = useCallback(async () => {
    if (files.length === 0 || isRendering) return
    setIsRendering(true)
    let exported = 0
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        setStatus(`Rendering ${i + 1} of ${files.length}: ${file.name}`)

        // Yield to the event loop before each render so the browser
        // releases the previous OfflineAudioContext resources fully
        await new Promise(r => setTimeout(r, 100))

        try {
          // Fresh snapshot + full render per file — identical to single Render (R)
          await renderAndDownload(file.buffer, file.name)
          exported++
        } catch (err) {
          console.error(`Render failed for ${file.name}:`, err)
        }

        // Extra breathing room between downloads
        if (i < files.length - 1) {
          await new Promise(r => setTimeout(r, 400))
        }
      }
      setStatus(`Exported ${exported} of ${files.length} files`)
    } catch (err) {
      console.error('Export all failed:', err)
      setStatus('Export failed — check console')
    } finally {
      setIsRendering(false)
    }
  }, [files, isRendering, renderAndDownload])

  const onBrowseClick = () => fileInputRef.current?.click()

  const handleResetApp = () => {
    if (!window.confirm('This will unload all files and reset all parameters to default. Are you sure?')) {
      return
    }
    
    // Stop audio
    engineRef.current.stop()
    setIsPlaying(false)
    
    // Clear files and loop state
    setFiles([])
    setSelectedIndex(-1)
    setLoopState({
      buffer: null,
      clips: [],
      bpm: DEFAULT_BPM,
      steps: DEFAULT_STEPS,
    })
    setFileName('')
    
    // Reset mix state
    setMixState({
      eq: { dryDb: -6, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: true, routingMode: 'parallel' },
      multiband: { dryDb: -6, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: true, routingMode: 'parallel' },
      compressor: { dryDb: -6, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: true, routingMode: 'parallel' },
      overdrive: { dryDb: -6, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: true, routingMode: 'parallel' },
      transient: { dryDb: -6, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: true, routingMode: 'parallel' },
      delay: { dryDb: 0, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: false, routingMode: 'parallel' },
      rsreverb: { dryDb: 0, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: false, routingMode: 'parallel' },
      reverb: { dryDb: 0, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: false, routingMode: 'parallel' },
      limiter: { dryDb: -6, wetDb: -6, drySolo: false, wetSolo: false, gangEnabled: false, inverseEnabled: true, routingMode: 'parallel' },
    })
    
    // Reset module enable states
    setModuleEnabled({
      eq: true,
      multiband: false,
      compressor: false,
      overdrive: false,
      transient: false,
      delay: false,
      rsreverb: false,
      reverb: false,
      limiter: false,
    })
    setActiveModule('eq')
    
    // Reset parameters
    setMasterDb(0)
    setLowShelfDb(0); setLowShelfFreq(80)
    setMid1Db(0); setMid1Freq(700); setMid1Q(0.5)
    setMid2Db(0); setMid2Freq(3000); setMid2Q(0.5)
    setHighShelfDb(0); setHighShelfFreq(8000)
    setHeatAmount(0)
    
    setMbLowMidFreq(200); setMbMidHighFreq(4000)
    setMbLowGain(0); setMbMidGain(0); setMbHighGain(0)
    setMbLowDrive(0); setMbMidDrive(0); setMbHighDrive(0)
    setMbLowType('tube-warm'); setMbMidType('tube-warm'); setMbHighType('tube-warm')
    setMbGlobalDrive(1)
    
    setCompType('peak'); setCompThreshold(0); setCompRatio(2)
    setCompAttack(30); setCompRelease(200); setCompMakeup(0)
    setCompKnee(0.4); setCompDistAmount(0); setCompDistMode('valve'); setCompDistPosition('post')
    
    setOdDriveType('tube-warm'); setOdDriveAmount(0)
    setOdTightHz(80); setOdToneHz(8000); setOdBias(0); setOdOutputDb(0)
    
    setTransAttackGain(0); setTransSustainGain(0); setTransHoldTime(0)
    setTransDecay(30); setTransOutputDb(0)
    setDelayMs(350); setDelayFeedback(0.35); setDelayMix(1)
    setDelayMode('stereo'); setDelaySyncEnabled(false); setDelayBpm(120)
    setDelayDivision('1/8'); setDelayTone(0.7); setDelaySaturation(0.15); setDelayWidth(1)
    setRevRoomType('medium-room'); setRevPredelay(25); setRevSize(0.5)
    setRevDecay(0.5); setRevDamping(0.6); setRevBandwidth(0.75)
    setRevDensity(0.6); setRevEarlyLate(0.5); setRevModRate(0.5); setRevModDepth(0.7); setRevOutputDb(0)
    setRsRoomSize(0.71); setRsPreDelay(8); setRsFeedback(0.48)
    setRsDamping(0.72); setRsDiffusion(0.45)
    setRsEarlySize(0.84); setRsEarlyDecay(0.23)
    setRsModRate(0.72); setRsModDepth(0.79)
    setRsHighCut(7620); setRsLowCut(65)
    setRsEarlyLateMix(0.54); setRsOutputDb(0)
    
    setLimThreshold(0); setLimCeiling(0); setLimRelease(100)
    setLimKnee(0.4); setLimMode('normal')
    
    setLimiterEnabled(false)
    setMasterBypassed(false)
    
    // Sync engine parameters (a bit tedious, but necessary to match UI state)
    const engine = engineRef.current
    engine.setEqEnabled(true)
    engine.setMultibandEnabled(false)
    engine.setCompEnabled(false)
    engine.setOverdriveEnabled(false)
    engine.setTransientEnabled(false)
    engine.setDelayEnabled(false)
    engine.setRsReverbEnabled(false)
    engine.setReverbEnabled(false)
    engine.setLimiterModuleEnabled(false)
    engine.setMasterLimiterEnabled(false)
    engine.setBypass(false)
    
    // Reset gains
    engine.setEqDryDb(-6); engine.setEqWetDb(-6)
    engine.setMultibandDryDb(-6); engine.setMultibandWetDb(-6)
    engine.setCompDryDb(-6); engine.setCompWetDb(-6)
    engine.setOverdriveDryDb(-6); engine.setOverdriveWetDb(-6)
    engine.setTransientDryDb(-6); engine.setTransientWetDb(-6)
    engine.setDelayDryDb(0); engine.setDelayWetDb(-6)
    engine.setRsReverbDryDb(0); engine.setRsReverbWetDb(-6)
    engine.setReverbDryDb(0); engine.setReverbWetDb(-6)
    engine.setLimiterDryDb(-6); engine.setLimiterWetDb(-6)
    
    // Reset EQ
    engine.setLowShelfGain(0); engine.setLowShelfFreq(80)
    engine.setMid1Gain(0); engine.setMid1Freq(700); engine.setMid1Q(0.5)
    engine.setMid2Gain(0); engine.setMid2Freq(3000); engine.setMid2Q(0.5)
    engine.setHighShelfGain(0); engine.setHighShelfFreq(8000)
    engine.setHeatAmount(0)
    
    // Reset Multiband
    engine.setMultibandLowMidFreq(200); engine.setMultibandMidHighFreq(4000)
    engine.setMultibandLowGain(0); engine.setMultibandMidGain(0); engine.setMultibandHighGain(0)
    engine.setMultibandLowDrive(0); engine.setMultibandMidDrive(0); engine.setMultibandHighDrive(0)
    engine.setMultibandGlobalDrive(1)
    
    // Reset Comp
    engine.setCompThreshold(0); engine.setCompRatio(2); engine.setCompMakeup(0)
    engine.setCompAttack(30); engine.setCompRelease(200); engine.setCompKnee(0.4)
    engine.setCompDistAmount(0)
    
    // Reset Overdrive
    engine.setOverdriveDrive(0); engine.setOverdriveTightHz(80); engine.setOverdriveToneHz(8000)
    engine.setOverdriveBias(0); engine.setOverdriveOutputDb(0)
    
    // Reset Transient
    engine.setTransientAttackGain(0); engine.setTransientSustainGain(0)
    engine.setTransientHoldTime(0); engine.setTransientDecay(30); engine.setTransientOutputDb(0)
    engine.setDelayTimeMs(350); engine.setDelayFeedback(0.35); engine.setDelayMix(1)
    engine.setDelayMode('stereo'); engine.setDelaySync(false); engine.setDelayBpm(120)
    engine.setDelayDivision('1/8'); engine.setDelayTone(0.7); engine.setDelaySaturation(0.15); engine.setDelayWidth(1)
    // Reset Reverb
    engine.setReverbRoomType('medium-room'); engine.setReverbPredelay(25)
    engine.setReverbSize(0.5); engine.setReverbDecay(0.5)
    engine.setReverbDamping(0.6); engine.setReverbBandwidth(0.75)
    engine.setReverbDensity(0.6); engine.setReverbEarlyLate(0.5)
    engine.setReverbModRate(0.5); engine.setReverbModDepth(0.7)
    engine.setReverbOutputDb(0)
    engine.setRsRoomSize(0.71); engine.setRsPreDelay(8); engine.setRsFeedback(0.48)
    engine.setRsDamping(0.72); engine.setRsDiffusion(0.45)
    engine.setRsEarlySize(0.84); engine.setRsEarlyDecay(0.23)
    engine.setRsModRate(0.72); engine.setRsModDepth(0.79)
    engine.setRsHighCut(7620); engine.setRsLowCut(65)
    engine.setRsEarlyLateMix(0.54); engine.setRsOutputDb(0)
    
    // Reset Limiter
    engine.setLimiterThreshold(0); engine.setLimiterCeiling(0)
    engine.setLimiterRelease(100); engine.setLimiterKnee(0.4)
    
    setStatus('App reset.')
  }

  // Global keyboard shortcuts — capture phase so we always handle before focused elements
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Only skip when user is typing text (Space would insert a character)
      if (isTextInputElement(e.target)) return

      if (e.code === 'Space') {
        e.preventDefault()
        e.stopPropagation()
        toggleTransport()
        return
      }
      if (e.code === 'KeyR' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault()
        e.stopPropagation()
        handleRender()
        return
      }
      if (e.code === 'ArrowUp') {
        e.preventDefault()
        if (selectedIndex > 0) selectFile(selectedIndex - 1)
      } else if (e.code === 'ArrowDown') {
        e.preventDefault()
        if (selectedIndex < files.length - 1) selectFile(selectedIndex + 1)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [toggleTransport, handleRender, selectedIndex, files, selectFile])

  // Blur controls on pointer/touch release so Space always triggers shortcuts
  useEffect(() => {
    const onRelease = () => {
      const el = document.activeElement
      if (el && !isTextInputElement(el)) {
        ;(el as HTMLElement).blur()
      }
    }
    window.addEventListener('pointerup', onRelease)
    window.addEventListener('touchend', onRelease)
    return () => {
      window.removeEventListener('pointerup', onRelease)
      window.removeEventListener('touchend', onRelease)
    }
  }, [])

  // Track DSP left panel height for debugging/clamping
  useEffect(() => {
    const updateHeight = () => {
      if (dspLeftRef.current) {
        setDspLeftHeight(Math.round(dspLeftRef.current.offsetHeight))
      }
    }
    
    updateHeight()
    const ro = new ResizeObserver(updateHeight)
    if (dspLeftRef.current) ro.observe(dspLeftRef.current)
    return () => ro.disconnect()
  }, [activeModule])

  const handleRoutingChange = (mode: RoutingMode) => {
    const moduleId = activeModule
    setMixState((prev) => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        routingMode: mode,
        dryDb: mode === 'serial' ? Number.NEGATIVE_INFINITY : -6,
        wetDb: mode === 'serial' ? 0 : -6,
      },
    }))
    if (moduleId === 'eq') {
      engineRef.current.setRoutingMode(mode)
    }
    applyModuleMixGains(moduleId, mode === 'serial' ? Number.NEGATIVE_INFINITY : -6, mode === 'serial' ? 0 : -6, mixState[moduleId].drySolo, mixState[moduleId].wetSolo)
  }

  const handleDryChange = (db: number) => {
    const clamped = Math.min(MIX_MAX_DB, Math.max(MIX_MIN_DB, db))
    const state = mixState[activeModule]
    let nextWet = state.wetDb
    if (state.gangEnabled || state.inverseEnabled) {
      const delta = clamped - state.dryDb
      nextWet = state.inverseEnabled ? state.wetDb - delta : state.wetDb + delta
      nextWet = Math.min(MIX_MAX_DB, Math.max(MIX_MIN_DB, nextWet))
    }
    setMixState((prev) => ({
      ...prev,
      [activeModule]: {
        ...prev[activeModule],
        dryDb: clamped,
        wetDb: nextWet,
      },
    }))
    applyModuleMixGains(activeModule, clamped, nextWet, state.drySolo, state.wetSolo)
  }

  const handleWetChange = (db: number) => {
    const clamped = Math.min(MIX_MAX_DB, Math.max(MIX_MIN_DB, db))
    const state = mixState[activeModule]
    let nextDry = state.dryDb
    if (state.gangEnabled || state.inverseEnabled) {
      const delta = clamped - state.wetDb
      nextDry = state.inverseEnabled ? state.dryDb - delta : state.dryDb + delta
      nextDry = Math.min(MIX_MAX_DB, Math.max(MIX_MIN_DB, nextDry))
    }
    setMixState((prev) => ({
      ...prev,
      [activeModule]: {
        ...prev[activeModule],
        wetDb: clamped,
        dryDb: nextDry,
      },
    }))
    applyModuleMixGains(activeModule, nextDry, clamped, state.drySolo, state.wetSolo)
  }

  const handleLowShelfChange = (db: number) => {
    setLowShelfDb(db)
    engineRef.current.setLowShelfGain(db)
  }

  const handleHighShelfChange = (db: number) => {
    setHighShelfDb(db)
    engineRef.current.setHighShelfGain(db)
  }

  const handleMid1Change = (db: number) => {
    setMid1Db(db)
    engineRef.current.setMid1Gain(db)
  }

  const handleMid2Change = (db: number) => {
    setMid2Db(db)
    engineRef.current.setMid2Gain(db)
  }

  const handleLowShelfFreqChange = (freq: number) => {
    setLowShelfFreq(freq)
    engineRef.current.setLowShelfFreq(freq)
  }

  const handleMid1FreqChange = (freq: number) => {
    setMid1Freq(freq)
    engineRef.current.setMid1Freq(freq)
  }

  const handleMid2FreqChange = (freq: number) => {
    setMid2Freq(freq)
    engineRef.current.setMid2Freq(freq)
  }

  const handleHighShelfFreqChange = (freq: number) => {
    setHighShelfFreq(freq)
    engineRef.current.setHighShelfFreq(freq)
  }

  const handleMid1QChange = (value: number) => {
    setMid1Q(value)
    engineRef.current.setMid1Q(value)
  }

  const handleMid2QChange = (value: number) => {
    setMid2Q(value)
    engineRef.current.setMid2Q(value)
  }

  const handleHeatChange = (value: number) => {
    setHeatAmount(value)
    engineRef.current.setHeatAmount(value)
  }

  const handleMbLowMidFreqChange = (value: number) => {
    setMbLowMidFreq(value)
    engineRef.current.setMultibandLowMidFreq(value)
  }

  const handleMbMidHighFreqChange = (value: number) => {
    setMbMidHighFreq(value)
    engineRef.current.setMultibandMidHighFreq(value)
  }

  const handleMbLowGainChange = (value: number) => {
    setMbLowGain(value)
    engineRef.current.setMultibandLowGain(value)
  }

  const handleMbMidGainChange = (value: number) => {
    setMbMidGain(value)
    engineRef.current.setMultibandMidGain(value)
  }

  const handleMbHighGainChange = (value: number) => {
    setMbHighGain(value)
    engineRef.current.setMultibandHighGain(value)
  }

  const handleMbLowDriveChange = (value: number) => {
    setMbLowDrive(value)
    engineRef.current.setMultibandLowDrive(value)
  }

  const handleMbMidDriveChange = (value: number) => {
    setMbMidDrive(value)
    engineRef.current.setMultibandMidDrive(value)
  }

  const handleMbHighDriveChange = (value: number) => {
    setMbHighDrive(value)
    engineRef.current.setMultibandHighDrive(value)
  }

  const handleMbLowTypeChange = (value: string) => {
    setMbLowType(value)
    engineRef.current.setMultibandLowType(value)
  }

  const handleMbMidTypeChange = (value: string) => {
    setMbMidType(value)
    engineRef.current.setMultibandMidType(value)
  }

  const handleMbHighTypeChange = (value: string) => {
    setMbHighType(value)
    engineRef.current.setMultibandHighType(value)
  }

  const handleMbGlobalDriveChange = (value: number) => {
    setMbGlobalDrive(value)
    engineRef.current.setMultibandGlobalDrive(value)
  }

  const handleCompTypeChange = (value: 'peak' | 'rms' | 'peak-dynamics-lo' | 'rms-dynamics-lo') => {
    setCompType(value)
    engineRef.current.setCompType(value)
  }

  const handleCompThresholdChange = (value: number) => {
    setCompThreshold(value)
    engineRef.current.setCompThreshold(value)
  }

  const handleCompRatioChange = (value: number) => {
    setCompRatio(value)
    engineRef.current.setCompRatio(value)
  }

  const handleCompAttackChange = (value: number) => {
    setCompAttack(value)
    engineRef.current.setCompAttack(value)
  }

  const handleCompReleaseChange = (value: number) => {
    setCompRelease(value)
    engineRef.current.setCompRelease(value)
  }

  const handleCompMakeupChange = (value: number) => {
    setCompMakeup(value)
    engineRef.current.setCompMakeup(value)
  }

  const handleCompKneeChange = (value: number) => {
    setCompKnee(value)
    engineRef.current.setCompKnee(value)
  }

  const handleCompDistAmountChange = (value: number) => {
    setCompDistAmount(value)
    engineRef.current.setCompDistAmount(value)
  }

  const handleCompDistModeChange = (value: 'valve' | 'solid') => {
    setCompDistMode(value)
    engineRef.current.setCompDistMode(value)
  }

  const handleCompDistPositionChange = (value: 'pre' | 'post') => {
    setCompDistPosition(value)
    engineRef.current.setCompDistPosition(value)
  }

  const handleOdDriveTypeChange = (value: string) => {
    setOdDriveType(value)
    engineRef.current.setOverdriveType(value)
  }

  const handleOdDriveAmountChange = (value: number) => {
    setOdDriveAmount(value)
    engineRef.current.setOverdriveDrive(value)
  }

  const handleOdTightChange = (value: number) => {
    setOdTightHz(value)
    engineRef.current.setOverdriveTightHz(value)
  }

  const handleOdToneChange = (value: number) => {
    setOdToneHz(value)
    engineRef.current.setOverdriveToneHz(value)
  }

  const handleOdBiasChange = (value: number) => {
    setOdBias(value)
    engineRef.current.setOverdriveBias(value)
  }

  const handleOdOutputChange = (value: number) => {
    setOdOutputDb(value)
    engineRef.current.setOverdriveOutputDb(value)
  }

  const handleTransAttackGainChange = (value: number) => {
    setTransAttackGain(value)
    engineRef.current.setTransientAttackGain(value)
  }

  const handleTransSustainGainChange = (value: number) => {
    setTransSustainGain(value)
    engineRef.current.setTransientSustainGain(value)
  }

  const handleTransHoldTimeChange = (value: number) => {
    setTransHoldTime(value)
    engineRef.current.setTransientHoldTime(value)
  }

  const handleTransDecayChange = (value: number) => {
    setTransDecay(value)
    engineRef.current.setTransientDecay(value)
  }

  const handleTransOutputChange = (value: number) => {
    setTransOutputDb(value)
    engineRef.current.setTransientOutputDb(value)
  }

  const handleDelayMsChange = (value: number) => {
    setDelayMs(value)
    engineRef.current.setDelayTimeMs(value)
  }
  const handleDelayFeedbackChange = (value: number) => {
    setDelayFeedback(value)
    engineRef.current.setDelayFeedback(value)
  }
  const handleDelayMixChange = (value: number) => {
    setDelayMix(value)
    engineRef.current.setDelayMix(value)
  }
  const handleDelayModeChange = (mode: 'stereo' | 'pingpong') => {
    setDelayMode(mode)
    engineRef.current.setDelayMode(mode)
  }
  const handleDelaySyncChange = (enabled: boolean) => {
    setDelaySyncEnabled(enabled)
    engineRef.current.setDelaySync(enabled)
  }
  const handleDelayBpmChange = (value: number) => {
    setDelayBpm(value)
    engineRef.current.setDelayBpm(value)
  }
  const handleDelayDivisionChange = (value: string) => {
    setDelayDivision(value)
    engineRef.current.setDelayDivision(value)
  }
  const handleDelayToneChange = (value: number) => {
    setDelayTone(value)
    engineRef.current.setDelayTone(value)
  }
  const handleDelaySaturationChange = (value: number) => {
    setDelaySaturation(value)
    engineRef.current.setDelaySaturation(value)
  }
  const handleDelayWidthChange = (value: number) => {
    setDelayWidth(value)
    engineRef.current.setDelayWidth(value)
  }

  // ─── Reverb handlers ──────────────────────────────────────────────────────

  const handleRevRoomTypeChange = (value: string) => {
    setRevRoomType(value)
    engineRef.current.setReverbRoomType(value)
    // Also update local knob state to reflect the preset
    const presets: Record<string, { predelay: number; size: number; decay: number; damping: number; bandwidth: number; density: number; earlyLate: number; modRate: number; modDepth: number }> = {
      'small-room':  { predelay: 10,  size: 0.25, decay: 0.30, damping: 0.70, bandwidth: 0.80, density: 0.50, earlyLate: 0.35, modRate: 0.35, modDepth: 0.45 },
      'medium-room': { predelay: 25,  size: 0.50, decay: 0.50, damping: 0.60, bandwidth: 0.75, density: 0.60, earlyLate: 0.50, modRate: 0.50, modDepth: 0.70 },
      'large-hall':  { predelay: 50,  size: 0.85, decay: 0.75, damping: 0.45, bandwidth: 0.70, density: 0.70, earlyLate: 0.72, modRate: 0.40, modDepth: 1.10 },
      'plate':       { predelay: 5,   size: 0.40, decay: 0.60, damping: 0.30, bandwidth: 0.90, density: 0.85, earlyLate: 0.82, modRate: 0.80, modDepth: 0.55 },
      'cathedral':   { predelay: 75,  size: 0.95, decay: 0.90, damping: 0.55, bandwidth: 0.60, density: 0.80, earlyLate: 0.88, modRate: 0.30, modDepth: 1.30 },
      'chamber':     { predelay: 20,  size: 0.55, decay: 0.55, damping: 0.50, bandwidth: 0.80, density: 0.65, earlyLate: 0.62, modRate: 0.55, modDepth: 0.75 },
    }
    const p = presets[value]
    if (p) {
      setRevPredelay(p.predelay); setRevSize(p.size); setRevDecay(p.decay)
      setRevDamping(p.damping); setRevBandwidth(p.bandwidth)
      setRevDensity(p.density); setRevEarlyLate(p.earlyLate)
      setRevModRate(p.modRate); setRevModDepth(p.modDepth)
      engineRef.current.setReverbPredelay(p.predelay)
      engineRef.current.setReverbSize(p.size)
      engineRef.current.setReverbDecay(p.decay)
      engineRef.current.setReverbDamping(p.damping)
      engineRef.current.setReverbBandwidth(p.bandwidth)
      engineRef.current.setReverbDensity(p.density)
      engineRef.current.setReverbEarlyLate(p.earlyLate)
      engineRef.current.setReverbModRate(p.modRate)
      engineRef.current.setReverbModDepth(p.modDepth)
    }
  }

  const handleRevPredelayChange = (value: number) => {
    setRevPredelay(value)
    engineRef.current.setReverbPredelay(value)
  }
  const handleRevSizeChange = (value: number) => {
    setRevSize(value)
    engineRef.current.setReverbSize(value)
  }
  const handleRevDecayChange = (value: number) => {
    setRevDecay(value)
    engineRef.current.setReverbDecay(value)
  }
  const handleRevDampingChange = (value: number) => {
    setRevDamping(value)
    engineRef.current.setReverbDamping(value)
  }
  const handleRevBandwidthChange = (value: number) => {
    setRevBandwidth(value)
    engineRef.current.setReverbBandwidth(value)
  }
  const handleRevDensityChange = (value: number) => {
    setRevDensity(value)
    engineRef.current.setReverbDensity(value)
  }
  const handleRevEarlyLateChange = (value: number) => {
    setRevEarlyLate(value)
    engineRef.current.setReverbEarlyLate(value)
  }
  const handleRevModRateChange = (value: number) => {
    setRevModRate(value)
    engineRef.current.setReverbModRate(value)
  }
  const handleRevModDepthChange = (value: number) => {
    setRevModDepth(value)
    engineRef.current.setReverbModDepth(value)
  }
  const handleRevOutputDbChange = (value: number) => {
    setRevOutputDb(value)
    engineRef.current.setReverbOutputDb(value)
  }

  const handleRsRoomSizeChange = (value: number) => {
    setRsRoomSize(value)
    engineRef.current.setRsRoomSize(value)
  }
  const handleRsPreDelayChange = (value: number) => {
    setRsPreDelay(value)
    engineRef.current.setRsPreDelay(value)
  }
  const handleRsFeedbackChange = (value: number) => {
    setRsFeedback(value)
    engineRef.current.setRsFeedback(value)
  }
  const handleRsDampingChange = (value: number) => {
    setRsDamping(value)
    engineRef.current.setRsDamping(value)
  }
  const handleRsDiffusionChange = (value: number) => {
    setRsDiffusion(value)
    engineRef.current.setRsDiffusion(value)
  }
  const handleRsEarlySizeChange = (value: number) => {
    setRsEarlySize(value)
    engineRef.current.setRsEarlySize(value)
  }
  const handleRsEarlyDecayChange = (value: number) => {
    setRsEarlyDecay(value)
    engineRef.current.setRsEarlyDecay(value)
  }
  const handleRsModRateChange = (value: number) => {
    setRsModRate(value)
    engineRef.current.setRsModRate(value)
  }
  const handleRsModDepthChange = (value: number) => {
    setRsModDepth(value)
    engineRef.current.setRsModDepth(value)
  }
  const handleRsHighCutChange = (value: number) => {
    setRsHighCut(value)
    engineRef.current.setRsHighCut(value)
  }
  const handleRsLowCutChange = (value: number) => {
    setRsLowCut(value)
    engineRef.current.setRsLowCut(value)
  }
  const handleRsEarlyLateMixChange = (value: number) => {
    setRsEarlyLateMix(value)
    engineRef.current.setRsEarlyLateMix(value)
  }
  const handleRsOutputDbChange = (value: number) => {
    setRsOutputDb(value)
    engineRef.current.setRsOutputDb(value)
  }

  const handleLimiterThresholdChange = (value: number) => {
    setLimThreshold(value)
    engineRef.current.setLimiterThreshold(value)
  }

  const handleLimiterCeilingChange = (value: number) => {
    setLimCeiling(value)
    engineRef.current.setLimiterCeiling(value)
  }

  const handleLimiterReleaseChange = (value: number) => {
    setLimRelease(value)
    engineRef.current.setLimiterRelease(value)
  }

  const handleLimiterKneeChange = (value: number) => {
    setLimKnee(value)
    engineRef.current.setLimiterKnee(value)
  }

  const handleLimiterModeChange = (value: 'pumping' | 'normal' | 'smooth') => {
    setLimMode(value)
    engineRef.current.setLimiterMode(value)
  }

  const handleMasterChange = (db: number) => {
    setMasterDb(db)
    engineRef.current.setMasterDb(db)
  }

  const toggleDrySolo = () => {
    const state = mixState[activeModule]
    const next = !state.drySolo
    setMixState((prev) => ({
      ...prev,
      [activeModule]: { ...prev[activeModule], drySolo: next },
    }))
    applyModuleMixGains(activeModule, state.dryDb, state.wetDb, next, state.wetSolo)
  }

  const toggleWetSolo = () => {
    const state = mixState[activeModule]
    const next = !state.wetSolo
    setMixState((prev) => ({
      ...prev,
      [activeModule]: { ...prev[activeModule], wetSolo: next },
    }))
    applyModuleMixGains(activeModule, state.dryDb, state.wetDb, state.drySolo, next)
  }

  const toggleGang = () => {
    setMixState((prev) => ({
      ...prev,
      [activeModule]: {
        ...prev[activeModule],
        gangEnabled: !prev[activeModule].gangEnabled,
        inverseEnabled: !prev[activeModule].gangEnabled ? false : prev[activeModule].inverseEnabled,
      },
    }))
  }

  const toggleInverse = () => {
    setMixState((prev) => ({
      ...prev,
      [activeModule]: {
        ...prev[activeModule],
        inverseEnabled: !prev[activeModule].inverseEnabled,
        gangEnabled: !prev[activeModule].inverseEnabled ? false : prev[activeModule].gangEnabled,
      },
    }))
  }

  const handleLimiterToggle = (enabled: boolean) => {
    setLimiterEnabled(enabled)
    engineRef.current.setMasterLimiterEnabled(enabled)
  }

  const toggleMasterBypass = () => {
    const next = !masterBypassed
    setMasterBypassed(next)
    engineRef.current.setBypass(next)
  }

  const handleModuleTabClick = (moduleId: ModuleId, event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.shiftKey) {
      const next = !moduleEnabled[moduleId]
      setModuleEnabled((prev) => ({ ...prev, [moduleId]: next }))
      if (moduleId === 'eq') {
        engineRef.current.setEqEnabled(next)
      } else if (moduleId === 'multiband') {
        engineRef.current.setMultibandEnabled(next)
      } else if (moduleId === 'compressor') {
        engineRef.current.setCompEnabled(next)
      } else if (moduleId === 'overdrive') {
        engineRef.current.setOverdriveEnabled(next)
      } else if (moduleId === 'transient') {
        engineRef.current.setTransientEnabled(next)
      } else if (moduleId === 'delay') {
        engineRef.current.setDelayEnabled(next)
      } else if (moduleId === 'rsreverb') {
        engineRef.current.setRsReverbEnabled(next)
      } else if (moduleId === 'reverb') {
        engineRef.current.setReverbEnabled(next)
      } else {
        engineRef.current.setLimiterModuleEnabled(next)
      }
      return
    }
    setActiveModule(moduleId)
  }

  useEffect(() => {
    const engine = engineRef.current as AudioEngine & {
      setOutputTrimDb?: (db: number) => void
      setMasterLimiterThreshold?: (db: number) => void
    }
    if (typeof engine.setOutputTrimDb === 'function') {
      engine.setOutputTrimDb(finalPeakDb)
    } else if (typeof engine.setMasterLimiterThreshold === 'function') {
      // Fallback for stale HMR instances
      engine.setMasterLimiterThreshold(finalPeakDb)
    }
  }, [finalPeakDb])

  // Update gain reduction meter
  useEffect(() => {
    if (!limiterEnabled || !isPlaying) {
      setGainReduction(0)
      return
    }

    const interval = setInterval(() => {
      const reduction = engineRef.current.getGainReduction()
      setGainReduction(reduction)
    }, 50) // Update every 50ms for smooth meter

    return () => clearInterval(interval)
  }, [limiterEnabled, isPlaying])

  // Update peak meters using requestAnimationFrame
  useEffect(() => {
    if (!isPlaying || !loopState.buffer) {
      setPeakDbLeft(-96)
      setPeakDbRight(-96)
      setPlayheadTime(null)
      setCompReductionDb(0)
      setLimReductionDb(0)
      return
    }

    // Set up level callback
    engineRef.current.onLevels((left, right) => {
      setPeakDbLeft(left)
      setPeakDbRight(right)
    })
    engineRef.current.onCompReduction((reduction) => {
      setCompReductionDb(reduction)
    })
    engineRef.current.onLimiterReduction((reduction) => {
      setLimReductionDb(reduction)
    })

    // Request levels on animation frame
    let raf = 0
    const tick = () => {
      engineRef.current.requestLevels()
      engineRef.current.requestCompReduction()
      engineRef.current.requestLimiterReduction()
      setPlayheadTime(engineRef.current.getPlaybackTime())
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
    }
  }, [isPlaying, loopState.buffer])

  const activeMix = mixState[activeModule]
  const dryResetDb = activeModule === 'reverb' || activeModule === 'rsreverb' || activeModule === 'delay' ? 0 : -6
  const activeRoutingMode = mixState[activeModule].routingMode
  const activeModuleEnabled = moduleEnabled[activeModule]
  const compReductionPercent = Math.max(0, Math.min(100, (-compReductionDb / 20) * 100))
  const limReductionPercent = Math.max(0, Math.min(100, (-limReductionDb / 20) * 100))

  return (
    <div className="app-shell" onDrop={onDrop} onDragOver={e => e.preventDefault()}>
      <header className="top-bar">
        <div className="brand">
          <img src="/f9_logo_64px.png" alt="F9" className="brand__logo" />
          <div>
            <div className="title">\\ Parallel DSP Playground</div>
            <div className="subtitle"></div>
          </div>
        </div>
        <div className="controls-row">
          <div className={`audio-ready ${audioReady ? 'audio-ready--on' : ''}`}>
            <span className="audio-ready__dot" />
            {audioReady ? 'Audio ready' : 'Audio suspended'}
          </div>
          <button className="ghost" onClick={() => setIsManualOpen(true)}>Instructions</button>
          <button
            className={`pill render-button ${isRendering ? 'render-button--active' : ''}`}
            onClick={handleRender}
            disabled={!loopState.buffer || isRendering}
            title="Render and download (R)"
          >
            {isRendering ? (
              <>
                <span className="render-spinner" />
                Rendering…
              </>
            ) : (
              'Render (R)'
            )}
          </button>
          <button className={`pill ${isPlaying ? 'danger' : 'accent'}`} onClick={toggleTransport}>
            {isPlaying ? 'Stop (Space)' : 'Play (Space)'}
          </button>
          <button
            className="icon-button"
            onClick={() => setIsSettingsOpen(true)}
            aria-label="Open settings"
            title="Settings"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Zm8.5 3.5a6.5 6.5 0 0 0-.08-1l2.02-1.58-2-3.46-2.46 1a7.1 7.1 0 0 0-1.72-1l-.37-2.6h-4l-.37 2.6a7.1 7.1 0 0 0-1.72 1l-2.46-1-2 3.46L3.58 11a6.5 6.5 0 0 0 0 2l-2.02 1.58 2 3.46 2.46-1a7.1 7.1 0 0 0 1.72 1l.37 2.6h4l.37-2.6a7.1 7.1 0 0 0 1.72-1l2.46 1 2-3.46L20.42 13c.05-.33.08-.66.08-1Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          multiple
          style={{ display: 'none' }}
          onChange={e => {
            if (e.target.files && e.target.files.length > 0) {
              processFiles(e.target.files)
            }
          }}
        />
      </header>

      <main className="layout">
        <aside className="sidebar">
          <FileList
            files={files}
            selectedIndex={selectedIndex}
            onSelect={selectFile}
            onExportAll={handleExportAll}
            onDrop={(fileList) => processFiles(fileList)}
            onLoadClick={onBrowseClick}
            onReset={handleResetApp}
          />
        </aside>
        
        <div className="main-content">
          <section className="waveform-panel">
          <div className="panel-header">
            <div>
              <div className="eyebrow">Waveform</div>
              <div className="panel-title">{fileName || 'Drop or load a WAV / AIFF loop'}</div>
            </div>
            <div className="badge">{loopState.buffer ? `${loopState.steps} steps @ ${loopState.bpm} BPM` : 'Idle'}</div>
          </div>
          <div className="waveform-frame">
            {loopState.buffer ? (
              <WaveformView buffer={loopState.buffer} playheadTime={playheadTime} />
            ) : (
              <div className="placeholder">Drop audio here or click Load</div>
            )}
          </div>
        </section>

        <section className="dsp-panel">
          <div className="tabs">
            <button
              className={`tab ${activeModule === 'eq' ? 'tab--active' : ''} ${moduleEnabled.eq ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('eq', event)}
              title={moduleEnabled.eq ? 'Click to edit EQ (Shift-click to disable)' : 'Click to edit EQ (Shift-click to enable)'}
            >
              EQ {moduleEnabled.eq ? '' : '(OFF)'}
            </button>
            <button
              className={`tab ${activeModule === 'multiband' ? 'tab--active' : ''} ${moduleEnabled.multiband ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('multiband', event)}
              title={moduleEnabled.multiband ? 'Click to edit Multiband (Shift-click to disable)' : 'Click to edit Multiband (Shift-click to enable)'}
            >
              Multiband {moduleEnabled.multiband ? '' : '(OFF)'}
            </button>
            <button
              className={`tab ${activeModule === 'compressor' ? 'tab--active' : ''} ${moduleEnabled.compressor ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('compressor', event)}
              title={moduleEnabled.compressor ? 'Click to edit Compressor (Shift-click to disable)' : 'Click to edit Compressor (Shift-click to enable)'}
            >
              Compressor {moduleEnabled.compressor ? '' : '(OFF)'}
            </button>
            <button
              className={`tab ${activeModule === 'overdrive' ? 'tab--active' : ''} ${moduleEnabled.overdrive ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('overdrive', event)}
              title={moduleEnabled.overdrive ? 'Click to edit Overdrive (Shift-click to disable)' : 'Click to edit Overdrive (Shift-click to enable)'}
            >
              Overdrive {moduleEnabled.overdrive ? '' : '(OFF)'}
            </button>
            <button
              className={`tab ${activeModule === 'transient' ? 'tab--active' : ''} ${moduleEnabled.transient ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('transient', event)}
              title={moduleEnabled.transient ? 'Click to edit Transient (Shift-click to disable)' : 'Click to edit Transient (Shift-click to enable)'}
            >
              Transient {moduleEnabled.transient ? '' : '(OFF)'}
            </button>
            <button
              className={`tab ${activeModule === 'delay' ? 'tab--active' : ''} ${moduleEnabled.delay ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('delay', event)}
              title={moduleEnabled.delay ? 'Click to edit Delay (Shift-click to disable)' : 'Click to edit Delay (Shift-click to enable)'}
            >
              Delay {moduleEnabled.delay ? '' : '(OFF)'}
            </button>
            <button
              className={`tab ${activeModule === 'rsreverb' ? 'tab--active' : ''} ${moduleEnabled.rsreverb ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('rsreverb', event)}
              title={moduleEnabled.rsreverb ? 'Click to edit RS Hall Reverb (Shift-click to disable)' : 'Click to edit RS Hall Reverb (Shift-click to enable)'}
            >
              RS Hall {moduleEnabled.rsreverb ? '' : '(OFF)'}
            </button>
            <button
              className={`tab ${activeModule === 'reverb' ? 'tab--active' : ''} ${moduleEnabled.reverb ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('reverb', event)}
              title={moduleEnabled.reverb ? 'Click to edit Reverb (Shift-click to disable)' : 'Click to edit Reverb (Shift-click to enable)'}
            >
              Reverb {moduleEnabled.reverb ? '' : '(OFF)'}
            </button>
            <button
              className={`tab ${activeModule === 'limiter' ? 'tab--active' : ''} ${moduleEnabled.limiter ? '' : 'tab--bypassed'}`}
              onClick={(event) => handleModuleTabClick('limiter', event)}
              title={moduleEnabled.limiter ? 'Click to edit Limiter (Shift-click to disable)' : 'Click to edit Limiter (Shift-click to enable)'}
            >
              Limiter {moduleEnabled.limiter ? '' : '(OFF)'}
            </button>
          </div>
          <div className="dsp-body">
            <div className="dsp-left" ref={dspLeftRef}>
              <div className="dsp-left__height-badge">{dspLeftHeight}px</div>
              {activeModule === 'eq' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.eq.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.eq.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                  <div className="eq-header">
                    <div className="section-title">EQ Parameters</div>
                    <EQCurve
                      lowGainDb={lowShelfDb}
                      lowFreq={lowShelfFreq}
                      mid1GainDb={mid1Db}
                      mid1Freq={mid1Freq}
                      mid1Q={mid1Q}
                      mid2GainDb={mid2Db}
                      mid2Freq={mid2Freq}
                      mid2Q={mid2Q}
                      highGainDb={highShelfDb}
                      highFreq={highShelfFreq}
                      getSpectrum={(bands: number) => engineRef.current.getEqSpectrum(bands)}
                    />
                  </div>
                  <div className="eq-controls">
                <div className="eq-band">
                  <div className="eq-band__label">Low Shelf</div>
                  <div className="eq-band__knobs">
                    <Knob 
                      label="Gain" 
                      value={lowShelfDb} 
                      onChange={handleLowShelfChange}
                      min={-12}
                      max={12}
                      step={0.1}
                      accent="lightBlue"
                      defaultValue={0}
                      formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                    />
                    <Knob 
                      label="Freq" 
                      value={lowShelfFreq} 
                      onChange={handleLowShelfFreqChange}
                      min={20}
                      max={500}
                      step={1}
                      unit="Hz"
                      accent="lightBlue"
                      defaultValue={80}
                    />
                  </div>
                </div>
                <div className="eq-band">
                  <div className="eq-band__label">Mid 1</div>
                  <div className="eq-band__knobs">
                    <Knob 
                      label="Gain" 
                      value={mid1Db} 
                      onChange={handleMid1Change}
                      min={-15}
                      max={15}
                      step={0.1}
                      accent="orange"
                      defaultValue={0}
                      formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                    />
                    <Knob 
                      label="Freq" 
                      value={mid1Freq} 
                      onChange={handleMid1FreqChange}
                      min={200}
                      max={1000}
                      step={10}
                      unit="Hz"
                      accent="orange"
                      defaultValue={700}
                    />
                    <Knob 
                      label="Q" 
                      value={mid1Q} 
                      onChange={handleMid1QChange}
                      min={0.3}
                      max={6}
                      step={0.1}
                      unit="Q"
                      accent="orange"
                      defaultValue={0.5}
                    />
                  </div>
                </div>
                <div className="eq-band">
                  <div className="eq-band__label">Mid 2</div>
                  <div className="eq-band__knobs">
                    <Knob 
                      label="Gain" 
                      value={mid2Db} 
                      onChange={handleMid2Change}
                      min={-15}
                      max={15}
                      step={0.1}
                      accent="orange"
                      defaultValue={0}
                      formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                    />
                    <Knob 
                      label="Freq" 
                      value={mid2Freq} 
                      onChange={handleMid2FreqChange}
                      min={1200}
                      max={6000}
                      step={50}
                      unit="Hz"
                      accent="orange"
                      defaultValue={3000}
                    />
                    <Knob 
                      label="Q" 
                      value={mid2Q} 
                      onChange={handleMid2QChange}
                      min={0.3}
                      max={6}
                      step={0.1}
                      unit="Q"
                      accent="orange"
                      defaultValue={0.5}
                    />
                  </div>
                </div>
                <div className="eq-band">
                  <div className="eq-band__label">High Shelf</div>
                  <div className="eq-band__knobs">
                    <Knob 
                      label="Gain" 
                      value={highShelfDb} 
                      onChange={handleHighShelfChange}
                      min={-12}
                      max={12}
                      step={0.1}
                      accent="red"
                      defaultValue={0}
                      formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                    />
                    <Knob 
                      label="Freq" 
                      value={highShelfFreq} 
                      onChange={handleHighShelfFreqChange}
                      min={2000}
                      max={16000}
                      step={100}
                      unit="Hz"
                      accent="red"
                      defaultValue={8000}
                    />
                  </div>
                </div>
                <div className="eq-band">
                  <div className="eq-band__label">Heat</div>
                  <div className="eq-band__knobs">
                    <Knob 
                      label="Drive" 
                      value={heatAmount} 
                      onChange={handleHeatChange}
                      min={0}
                      max={1}
                      step={0.01}
                      unit=""
                      accent="red"
                      defaultValue={0}
                      formatValue={(val) => `${Math.round(val * 100)}%`}
                    />
                  </div>
                </div>
              </div>
                </>
              )}
              {activeModule === 'multiband' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.multiband.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.multiband.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                  <div className="eq-header">
                    <div className="section-title">Multiband Splitter</div>
                  </div>
                  <div className="module-body">
                    <div className="eq-controls">
                      <MultibandView
                        lowMidFreq={mbLowMidFreq}
                        midHighFreq={mbMidHighFreq}
                        lowGainDb={mbLowGain}
                        midGainDb={mbMidGain}
                        highGainDb={mbHighGain}
                        onLowMidFreqChange={handleMbLowMidFreqChange}
                        onMidHighFreqChange={handleMbMidHighFreqChange}
                        onLowGainChange={handleMbLowGainChange}
                        onMidGainChange={handleMbMidGainChange}
                        onHighGainChange={handleMbHighGainChange}
                        getSpectrum={(bands: number) => engineRef.current.getMultibandSpectrum(bands)}
                      />
                      <div className="eq-band">
                        <div className="eq-band__label">Global Drive</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Trim"
                            value={mbGlobalDrive}
                            onChange={handleMbGlobalDriveChange}
                            min={0}
                            max={2}
                            step={0.01}
                            accent="red"
                            defaultValue={1}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Low Band</div>
                        <div className="eq-band__knobs">
                          <div className="module-select">
                            <label className="module-select__label" htmlFor="mbLowType">Model</label>
                            <select
                              id="mbLowType"
                              value={mbLowType}
                              onChange={(event) => handleMbLowTypeChange(event.target.value)}
                            >
                              <option value="tube-warm">Tube Warm</option>
                              <option value="tube-drive">Tube Drive</option>
                              <option value="tube-hot">Tube Hot</option>
                              <option value="overdrive">Overdrive</option>
                              <option value="hard">Hard Clip</option>
                              <option value="fuzz">Fuzz</option>
                              <option value="rat">Rat</option>
                              <option value="muff">Muff</option>
                              <option value="range">Rangemaster</option>
                            </select>
                          </div>
                          <Knob
                            label="Drive"
                            value={mbLowDrive}
                            onChange={handleMbLowDriveChange}
                            min={0}
                            max={1.5}
                            step={0.01}
                            accent="red"
                            defaultValue={0}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Mid Band</div>
                        <div className="eq-band__knobs">
                          <div className="module-select">
                            <label className="module-select__label" htmlFor="mbMidType">Model</label>
                            <select
                              id="mbMidType"
                              value={mbMidType}
                              onChange={(event) => handleMbMidTypeChange(event.target.value)}
                            >
                              <option value="tube-warm">Tube Warm</option>
                              <option value="tube-drive">Tube Drive</option>
                              <option value="tube-hot">Tube Hot</option>
                              <option value="overdrive">Overdrive</option>
                              <option value="hard">Hard Clip</option>
                              <option value="fuzz">Fuzz</option>
                              <option value="rat">Rat</option>
                              <option value="muff">Muff</option>
                              <option value="range">Rangemaster</option>
                            </select>
                          </div>
                          <Knob
                            label="Drive"
                            value={mbMidDrive}
                            onChange={handleMbMidDriveChange}
                            min={0}
                            max={1.5}
                            step={0.01}
                            accent="red"
                            defaultValue={0}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">High Band</div>
                        <div className="eq-band__knobs">
                          <div className="module-select">
                            <label className="module-select__label" htmlFor="mbHighType">Model</label>
                            <select
                              id="mbHighType"
                              value={mbHighType}
                              onChange={(event) => handleMbHighTypeChange(event.target.value)}
                            >
                              <option value="tube-warm">Tube Warm</option>
                              <option value="tube-drive">Tube Drive</option>
                              <option value="tube-hot">Tube Hot</option>
                              <option value="overdrive">Overdrive</option>
                              <option value="hard">Hard Clip</option>
                              <option value="fuzz">Fuzz</option>
                              <option value="rat">Rat</option>
                              <option value="muff">Muff</option>
                              <option value="range">Rangemaster</option>
                            </select>
                          </div>
                          <Knob
                            label="Drive"
                            value={mbHighDrive}
                            onChange={handleMbHighDriveChange}
                            min={0}
                            max={1.5}
                            step={0.01}
                            accent="red"
                            defaultValue={0}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {activeModule === 'compressor' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.compressor.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.compressor.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                  <div className="eq-header">
                    <div className="section-title">Compressor</div>
                  </div>
                  <div className="module-body">
                    <div className="eq-controls">
                    <div className="eq-band">
                      <div className="eq-band__label">Type</div>
                      <div className="eq-band__knobs">
                        <div className="module-select">
                          <select
                            id="compType"
                            value={compType}
                            onChange={(event) => handleCompTypeChange(event.target.value as typeof compType)}
                          >
                            <option value="peak">Peak Compressor</option>
                            <option value="rms">RMS Compressor</option>
                            <option value="peak-dynamics-lo">Peak DynamicsLo</option>
                            <option value="rms-dynamics-lo">RMS DynamicsLo</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="eq-band">
                      <div className="eq-band__label">Dynamics</div>
                      <div className="eq-band__knobs">
                        <Knob
                          label="Threshold"
                          value={compThreshold}
                          onChange={handleCompThresholdChange}
                          min={-48}
                          max={0}
                          step={0.1}
                          accent="lightBlue"
                          defaultValue={0}
                          formatValue={(val) => `${val.toFixed(1)}dB`}
                        />
                        <Knob
                          label="Ratio"
                          value={compRatio}
                          onChange={handleCompRatioChange}
                          min={1}
                          max={20}
                          step={0.1}
                          accent="orange"
                          defaultValue={2}
                          formatValue={(val) => `${val.toFixed(1)}:1`}
                        />
                        <Knob
                          label="Knee"
                          value={compKnee}
                          onChange={handleCompKneeChange}
                          min={0}
                          max={6}
                          step={0.1}
                          accent="yellow"
                          defaultValue={0.4}
                          formatValue={(val) => val.toFixed(1)}
                        />
                      </div>
                    </div>
                    <div className="eq-band">
                      <div className="eq-band__label">Timing</div>
                      <div className="eq-band__knobs">
                        <Knob
                          label="Attack"
                          value={compAttack}
                          onChange={handleCompAttackChange}
                          min={1}
                          max={150}
                          step={1}
                          unit="ms"
                          accent="orange"
                          defaultValue={30}
                          formatValue={(val) => `${Math.round(val)}ms`}
                        />
                        <Knob
                          label="Release"
                          value={compRelease}
                          onChange={handleCompReleaseChange}
                          min={20}
                          max={600}
                          step={1}
                          unit="ms"
                          accent="orange"
                          defaultValue={200}
                          formatValue={(val) => `${Math.round(val)}ms`}
                        />
                        <Knob
                          label="Makeup"
                          value={compMakeup}
                          onChange={handleCompMakeupChange}
                          min={-12}
                          max={12}
                          step={0.1}
                          accent="red"
                          defaultValue={0}
                          formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                        />
                      </div>
                    </div>
                    <div className="eq-band">
                      <div className="eq-band__label">Harmonics</div>
                      <div className="eq-band__knobs">
                        <Knob
                          label="Drive"
                          value={compDistAmount}
                          onChange={handleCompDistAmountChange}
                          min={0}
                          max={1}
                          step={0.01}
                          accent="red"
                          defaultValue={0}
                          formatValue={(val) => `${Math.round(val * 100)}%`}
                        />
                        <div className="module-select">
                          <label className="module-select__label" htmlFor="compDistMode">Style</label>
                          <select
                            id="compDistMode"
                            value={compDistMode}
                            onChange={(event) => handleCompDistModeChange(event.target.value as typeof compDistMode)}
                          >
                            <option value="valve">Valve</option>
                            <option value="solid">Solid State</option>
                          </select>
                        </div>
                        <div className="module-select">
                          <label className="module-select__label" htmlFor="compDistPosition">Position</label>
                          <select
                            id="compDistPosition"
                            value={compDistPosition}
                            onChange={(event) => handleCompDistPositionChange(event.target.value as typeof compDistPosition)}
                          >
                            <option value="pre">Pre</option>
                            <option value="post">Post</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    </div>
                    <div className="comp-meter">
                      <div className="comp-meter__label">GR</div>
                      <div className="comp-meter__bar">
                        <div
                          className="comp-meter__fill"
                          style={{ height: `${compReductionPercent}%` }}
                        />
                      </div>
                      <div className="comp-meter__value">
                        {Math.max(0, -compReductionDb).toFixed(1)} dB
                      </div>
                    </div>
                  </div>
                </>
              )}
              {activeModule === 'overdrive' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.overdrive.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.overdrive.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                  <div className="eq-header">
                    <div className="section-title">Overdrive</div>
                  </div>
                  <div className="module-body">
                    <div className="eq-controls">
                      <div className="eq-band">
                        <div className="eq-band__label">Drive</div>
                        <div className="eq-band__knobs">
                          <div className="module-select">
                            <label className="module-select__label" htmlFor="odDriveType">Model</label>
                            <select
                              id="odDriveType"
                              value={odDriveType}
                              onChange={(event) => handleOdDriveTypeChange(event.target.value)}
                            >
                              <option value="tube-warm">Tube Warm</option>
                              <option value="tube-drive">Tube Drive</option>
                              <option value="tube-hot">Tube Hot</option>
                              <option value="overdrive">Overdrive</option>
                              <option value="hard">Hard Clip</option>
                              <option value="fuzz">Fuzz</option>
                              <option value="rat">Rat</option>
                              <option value="muff">Muff</option>
                              <option value="range">Rangemaster</option>
                            </select>
                          </div>
                          <Knob
                            label="Drive"
                            value={odDriveAmount}
                            onChange={handleOdDriveAmountChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="red"
                            defaultValue={0}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Tone</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Tight"
                            value={odTightHz}
                            onChange={handleOdTightChange}
                            min={20}
                            max={600}
                            step={1}
                            unit="Hz"
                            accent="lightBlue"
                            defaultValue={80}
                            formatValue={(val) => `${Math.round(val)}Hz`}
                          />
                          <Knob
                            label="Tone"
                            value={odToneHz}
                            onChange={handleOdToneChange}
                            min={500}
                            max={16000}
                            step={10}
                            unit="Hz"
                            accent="orange"
                            defaultValue={8000}
                            formatValue={(val) => `${Math.round(val)}Hz`}
                          />
                          <Knob
                            label="Bias"
                            value={odBias}
                            onChange={handleOdBiasChange}
                            min={-0.5}
                            max={0.5}
                            step={0.01}
                            accent="yellow"
                            defaultValue={0}
                            formatValue={(val) => val.toFixed(2)}
                          />
                          <Knob
                            label="Output"
                            value={odOutputDb}
                            onChange={handleOdOutputChange}
                            min={-12}
                            max={12}
                            step={0.1}
                            accent="red"
                            defaultValue={0}
                            formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {activeModule === 'transient' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.transient.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.transient.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                  <div className="eq-header">
                    <div className="section-title">Transient Shaper</div>
                  </div>
                  <div className="module-body">
                    <div className="eq-controls">
                      <div className="eq-band">
                        <div className="eq-band__label">Shaping</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Attack"
                            value={transAttackGain}
                            onChange={handleTransAttackGainChange}
                            min={-24}
                            max={24}
                            step={0.1}
                            accent="red"
                            defaultValue={0}
                            formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                          />
                          <Knob
                            label="Sustain"
                            value={transSustainGain}
                            onChange={handleTransSustainGainChange}
                            min={-24}
                            max={24}
                            step={0.1}
                            accent="lightBlue"
                            defaultValue={0}
                            formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                          />
                          <Knob
                            label="Hold"
                            value={transHoldTime}
                            onChange={handleTransHoldTimeChange}
                            min={0}
                            max={100}
                            step={1}
                            unit="ms"
                            accent="yellow"
                            defaultValue={0}
                            formatValue={(val) => `${Math.round(val)}ms`}
                          />
                          <Knob
                            label="Decay"
                            value={transDecay}
                            onChange={handleTransDecayChange}
                            min={1}
                            max={500}
                            step={1}
                            unit="ms"
                            accent="orange"
                            defaultValue={30}
                            formatValue={(val) => `${Math.round(val)}ms`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Output</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Gain"
                            value={transOutputDb}
                            onChange={handleTransOutputChange}
                            min={-12}
                            max={12}
                            step={0.1}
                            accent="lightBlue"
                            defaultValue={0}
                            formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {activeModule === 'delay' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.delay.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.delay.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                  <div className="eq-header">
                    <div className="section-title">Delay</div>
                  </div>
                  <div className="module-body">
                    <div className="eq-controls">
                      <div className="eq-band">
                        <div className="eq-band__label">Time &amp; feedback</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Time"
                            value={delayMs}
                            onChange={handleDelayMsChange}
                            min={1}
                            max={2000}
                            step={1}
                            unit="ms"
                            accent="blue"
                            defaultValue={350}
                            formatValue={(val) => `${Math.round(val)}ms`}
                          />
                          <Knob
                            label="Feedback"
                            value={delayFeedback}
                            onChange={handleDelayFeedbackChange}
                            min={0}
                            max={0.98}
                            step={0.01}
                            accent="red"
                            defaultValue={0.35}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Mix"
                            value={delayMix}
                            onChange={handleDelayMixChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="lightBlue"
                            defaultValue={1}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Mode</div>
                        <div className="segmented" style={{ marginTop: 4 }}>
                          <button
                            className={delayMode === 'stereo' ? 'seg--active' : ''}
                            onClick={() => handleDelayModeChange('stereo')}
                          >
                            Stereo
                          </button>
                          <button
                            className={delayMode === 'pingpong' ? 'seg--active' : ''}
                            onClick={() => handleDelayModeChange('pingpong')}
                          >
                            Ping-pong
                          </button>
                        </div>
                        {delayMode === 'pingpong' && (
                          <div className="eq-band__knobs" style={{ marginTop: 8 }}>
                            <Knob
                              label="Width"
                              value={delayWidth}
                              onChange={handleDelayWidthChange}
                              min={0}
                              max={1}
                              step={0.01}
                              accent="yellow"
                              defaultValue={1}
                              formatValue={(val) => `${Math.round(val * 100)}%`}
                            />
                          </div>
                        )}
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Sync</div>
                        <div className="segmented" style={{ marginTop: 4 }}>
                          <button
                            className={!delaySyncEnabled ? 'seg--active' : ''}
                            onClick={() => handleDelaySyncChange(false)}
                          >
                            Free
                          </button>
                          <button
                            className={delaySyncEnabled ? 'seg--active' : ''}
                            onClick={() => handleDelaySyncChange(true)}
                          >
                            Sync
                          </button>
                        </div>
                        {delaySyncEnabled && (
                          <div className="eq-band__knobs" style={{ marginTop: 8 }}>
                            <Knob
                              label="BPM"
                              value={delayBpm}
                              onChange={handleDelayBpmChange}
                              min={20}
                              max={400}
                              step={1}
                              accent="orange"
                              defaultValue={120}
                              formatValue={(val) => `${Math.round(val)}`}
                            />
                            <select
                              value={delayDivision}
                              onChange={(e) => handleDelayDivisionChange(e.target.value)}
                              style={{ marginTop: 4, padding: '4px 8px', borderRadius: 6 }}
                            >
                              {['1/1', '1/2', '1/4', '1/8', '1/8D', '1/8T', '1/16', '1/16D', '1/16T'].map((d) => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Tape color</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Tone"
                            value={delayTone}
                            onChange={handleDelayToneChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="white"
                            defaultValue={0.7}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Saturation"
                            value={delaySaturation}
                            onChange={handleDelaySaturationChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="red"
                            defaultValue={0.15}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {activeModule === 'rsreverb' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.rsreverb.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.rsreverb.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                  <div className="eq-header">
                    <div className="section-title">RS Hall Reverb</div>
                  </div>
                  <div className="module-body">
                    <div className="eq-controls">
                      <div className="eq-band">
                        <div className="eq-band__label">Core</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Room Size"
                            value={rsRoomSize}
                            onChange={handleRsRoomSizeChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="orange"
                            defaultValue={0.71}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Feedback"
                            value={rsFeedback}
                            onChange={handleRsFeedbackChange}
                            min={0}
                            max={0.99}
                            step={0.01}
                            accent="red"
                            defaultValue={0.48}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Diffusion"
                            value={rsDiffusion}
                            onChange={handleRsDiffusionChange}
                            min={0}
                            max={0.99}
                            step={0.01}
                            accent="yellow"
                            defaultValue={0.45}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Timing</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Pre-Delay"
                            value={rsPreDelay}
                            onChange={handleRsPreDelayChange}
                            min={0}
                            max={150}
                            step={1}
                            unit="ms"
                            accent="lightBlue"
                            defaultValue={8}
                            formatValue={(val) => `${Math.round(val)}ms`}
                          />
                          <Knob
                            label="Mod Rate"
                            value={rsModRate}
                            onChange={handleRsModRateChange}
                            min={0}
                            max={5}
                            step={0.01}
                            unit="Hz"
                            accent="lightBlue"
                            defaultValue={0.72}
                            formatValue={(val) => `${val.toFixed(2)}Hz`}
                          />
                          <Knob
                            label="Mod Depth"
                            value={rsModDepth}
                            onChange={handleRsModDepthChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="lightBlue"
                            defaultValue={0.79}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Tone</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Damping"
                            value={rsDamping}
                            onChange={handleRsDampingChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="red"
                            defaultValue={0.72}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Low Cut"
                            value={rsLowCut}
                            onChange={handleRsLowCutChange}
                            min={20}
                            max={1000}
                            step={5}
                            unit="Hz"
                            accent="yellow"
                            defaultValue={65}
                            formatValue={(val) => `${Math.round(val)}Hz`}
                          />
                          <Knob
                            label="High Cut"
                            value={rsHighCut}
                            onChange={handleRsHighCutChange}
                            min={200}
                            max={20000}
                            step={20}
                            unit="Hz"
                            accent="yellow"
                            defaultValue={7620}
                            formatValue={(val) => `${Math.round(val)}Hz`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Early</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Early Size"
                            value={rsEarlySize}
                            onChange={handleRsEarlySizeChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="orange"
                            defaultValue={0.84}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Early Decay"
                            value={rsEarlyDecay}
                            onChange={handleRsEarlyDecayChange}
                            min={0}
                            max={0.99}
                            step={0.01}
                            accent="orange"
                            defaultValue={0.23}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Early/Late"
                            value={rsEarlyLateMix}
                            onChange={handleRsEarlyLateMixChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="yellow"
                            defaultValue={0.54}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Output</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Gain"
                            value={rsOutputDb}
                            onChange={handleRsOutputDbChange}
                            min={-24}
                            max={6}
                            step={0.1}
                            accent="lightBlue"
                            defaultValue={0}
                            formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {activeModule === 'reverb' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.reverb.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.reverb.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                <div className="eq-header">
                  <div className="section-title">Reverb</div>
                </div>
                  <div className="module-body">
                    <div className="eq-controls">
                      <div className="eq-band">
                        <div className="eq-band__label">Room</div>
                        <div className="eq-band__knobs">
                          <div className="module-select">
                            <label className="module-select__label" htmlFor="revRoomType">Type</label>
                            <select
                              id="revRoomType"
                              value={revRoomType}
                              onChange={(event) => handleRevRoomTypeChange(event.target.value)}
                            >
                              <option value="small-room">Small Room</option>
                              <option value="medium-room">Medium Room</option>
                              <option value="large-hall">Large Hall</option>
                              <option value="plate">Plate</option>
                              <option value="cathedral">Cathedral</option>
                              <option value="chamber">Chamber</option>
                            </select>
                          </div>
                          <Knob
                            label="Pre-Delay"
                            value={revPredelay}
                            onChange={handleRevPredelayChange}
                            min={0}
                            max={500}
                            step={1}
                            unit="ms"
                            accent="lightBlue"
                            defaultValue={25}
                            formatValue={(val) => `${Math.round(val)}ms`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Space</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Size"
                            value={revSize}
                            onChange={handleRevSizeChange}
                            min={0.05}
                            max={1}
                            step={0.01}
                            accent="orange"
                            defaultValue={0.5}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Decay"
                            value={revDecay}
                            onChange={handleRevDecayChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="orange"
                            defaultValue={0.5}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Density"
                            value={revDensity}
                            onChange={handleRevDensityChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="yellow"
                            defaultValue={0.6}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Tone</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Damping"
                            value={revDamping}
                            onChange={handleRevDampingChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="red"
                            defaultValue={0.6}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Bandwidth"
                            value={revBandwidth}
                            onChange={handleRevBandwidthChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="lightBlue"
                            defaultValue={0.75}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                          <Knob
                            label="Early/Late"
                            value={revEarlyLate}
                            onChange={handleRevEarlyLateChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="yellow"
                            defaultValue={0.5}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Output</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Mod Rate"
                            value={revModRate}
                            onChange={handleRevModRateChange}
                            min={0.1}
                            max={3}
                            step={0.01}
                            unit="Hz"
                            accent="orange"
                            defaultValue={0.5}
                            formatValue={(val) => `${val.toFixed(2)}Hz`}
                          />
                          <Knob
                            label="Mod Depth"
                            value={revModDepth}
                            onChange={handleRevModDepthChange}
                            min={0}
                            max={2}
                            step={0.01}
                            unit="ms"
                            accent="yellow"
                            defaultValue={0.7}
                            formatValue={(val) => `${val.toFixed(2)}ms`}
                          />
                          <Knob
                            label="Gain"
                            value={revOutputDb}
                            onChange={handleRevOutputDbChange}
                            min={-24}
                            max={6}
                            step={0.1}
                            accent="lightBlue"
                            defaultValue={0}
                            formatValue={(val) => `${val > 0 ? '+' : ''}${val.toFixed(1)}dB`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {activeModule === 'limiter' && (
                <>
                  <div className="segmented">
                    <button
                      className={mixState.limiter.routingMode === 'parallel' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('parallel')}
                    >
                      Parallel
                    </button>
                    <button
                      className={mixState.limiter.routingMode === 'serial' ? 'seg--active' : ''}
                      onClick={() => handleRoutingChange('serial')}
                    >
                      Serial
                    </button>
                  </div>
                  <div className="eq-header">
                    <div className="section-title">Limiter</div>
                  </div>
                  <div className="module-body">
                    <div className="eq-controls">
                      <div className="eq-band">
                        <div className="eq-band__label">Drive</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Threshold"
                            value={limThreshold}
                            onChange={handleLimiterThresholdChange}
                            min={-30}
                            max={0}
                            step={0.1}
                            accent="red"
                            defaultValue={0}
                            formatValue={(val) => `${val.toFixed(1)}dB`}
                          />
                          <Knob
                            label="Ceiling"
                            value={limCeiling}
                            onChange={handleLimiterCeilingChange}
                            min={-20}
                            max={0}
                            step={0.1}
                            accent="lightBlue"
                            defaultValue={0}
                            formatValue={(val) => `${val.toFixed(1)}dB`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Timing</div>
                        <div className="eq-band__knobs">
                          <Knob
                            label="Release"
                            value={limRelease}
                            onChange={handleLimiterReleaseChange}
                            min={1}
                            max={500}
                            step={1}
                            unit="ms"
                            accent="orange"
                            defaultValue={100}
                            formatValue={(val) => `${Math.round(val)}ms`}
                          />
                          <Knob
                            label="Knee"
                            value={limKnee}
                            onChange={handleLimiterKneeChange}
                            min={0}
                            max={1}
                            step={0.01}
                            accent="yellow"
                            defaultValue={0.4}
                            formatValue={(val) => `${Math.round(val * 100)}%`}
                          />
                        </div>
                      </div>
                      <div className="eq-band">
                        <div className="eq-band__label">Profile</div>
                        <div className="eq-band__knobs">
                          <div className="module-select">
                            <label className="module-select__label" htmlFor="limMode">Mode</label>
                            <select
                              id="limMode"
                              value={limMode}
                              onChange={(event) => handleLimiterModeChange(event.target.value as typeof limMode)}
                            >
                              <option value="pumping">Pumping</option>
                              <option value="normal">Normal</option>
                              <option value="smooth">Smooth</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="comp-meter">
                      <div className="comp-meter__label">GR</div>
                      <div className="comp-meter__bar">
                        <div
                          className="comp-meter__fill"
                          style={{ height: `${limReductionPercent}%` }}
                        />
                      </div>
                      <div className="comp-meter__value">
                        {Math.max(0, -limReductionDb).toFixed(1)} dB
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="dsp-right">
              <div className="parallel-mix-block">
                <div className="fader-pair">
                  <Fader
                    label="Dry"
                    db={activeMix.dryDb}
                    onChange={handleDryChange}
                    disabled={activeRoutingMode === 'serial' || !activeModuleEnabled}
                    showSolo
                    soloed={activeMix.drySolo}
                    onSoloToggle={toggleDrySolo}
                    onDoubleClick={() => { if (activeRoutingMode === 'parallel' && activeModuleEnabled) handleDryChange(dryResetDb) }}
                  />
                  <Fader
                    label="Wet"
                    db={activeMix.wetDb}
                    onChange={handleWetChange}
                    disabled={activeRoutingMode === 'serial' || !activeModuleEnabled}
                    showSolo
                    soloed={activeMix.wetSolo}
                    onSoloToggle={toggleWetSolo}
                    onDoubleClick={() => { if (activeRoutingMode === 'parallel' && activeModuleEnabled) handleWetChange(-6) }}
                  />
                </div>
                <div className="parallel-mix-title">Parallel Mix</div>
                <div className="fader-tools">
                  <button
                    type="button"
                    className={`fader-tool ${activeMix.gangEnabled ? 'fader-tool--active' : ''}`}
                    onClick={toggleGang}
                    aria-pressed={activeMix.gangEnabled}
                    aria-label="Gang faders"
                    title="Gang faders"
                    disabled={activeRoutingMode === 'serial' || !activeModuleEnabled}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path d="M8.5 12a3.5 3.5 0 0 1 3.5-3.5h3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <path d="M15.5 12a3.5 3.5 0 0 1-3.5 3.5h-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <rect x="3" y="6.5" width="7" height="11" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
                      <rect x="14" y="6.5" width="7" height="11" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
                    </svg>
                    <span className="fader-tool__label">Gang</span>
                  </button>
                  <button
                    type="button"
                    className={`fader-tool ${activeMix.inverseEnabled ? 'fader-tool--active' : ''}`}
                    onClick={toggleInverse}
                    aria-pressed={activeMix.inverseEnabled}
                    aria-label="Inverse faders"
                    title="Inverse faders"
                    disabled={activeRoutingMode === 'serial' || !activeModuleEnabled}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path d="M6 7h10l-2.5-2.5M18 17H8l2.5 2.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="fader-tool__label">Inverse</span>
                  </button>
                </div>
              </div>
              <div className="master-section">
                <div className="master-section__title">Master</div>
                <div className="master-section__body">
                  <div className="master-section__meters">
                    <VUMeter
                      label="L"
                      peakDb={peakDbLeft}
                      enabled={isPlaying && loopState.buffer !== null}
                      orientation="vertical"
                    />
                    <VUMeter
                      label="R"
                      peakDb={peakDbRight}
                      enabled={isPlaying && loopState.buffer !== null}
                      orientation="vertical"
                    />
                  </div>
                  <div className="master-section__controls">
                    <Fader
                      label="Master"
                      db={masterDb}
                      onChange={handleMasterChange}
                      minDb={MASTER_MIN_DB}
                      maxDb={MASTER_MAX_DB}
                      onDoubleClick={() => handleMasterChange(0)}
                    />
                    <button
                      className={`bypass-button ${masterBypassed ? 'bypass-button--active' : ''}`}
                      onClick={toggleMasterBypass}
                      aria-pressed={masterBypassed}
                    >
                      Master Bypass
                    </button>
                    <Limiter
                      enabled={limiterEnabled}
                      gainReduction={gainReduction}
                      onToggle={handleLimiterToggle}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        </div>
      </main>

      {isSettingsOpen && (
        <div className="modal-overlay" onClick={() => setIsSettingsOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">Settings</div>
              <button
                className="icon-button"
                onClick={() => setIsSettingsOpen(false)}
                aria-label="Close settings"
              >
                ✕
              </button>
            </div>
            <div className="modal__body">
              <div className="setting-row">
                <div>
                  <div className="setting-label">Absolute final peak</div>
                  <div className="setting-help">Post-limiter trim (dBFS)</div>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    min={FINAL_PEAK_MIN_DB}
                    max={FINAL_PEAK_MAX_DB}
                    step={0.1}
                    value={finalPeakDb}
                    onChange={e => setFinalPeakDb(parseFloat(e.target.value))}
                    aria-label="Absolute final peak"
                  />
                  <div className="setting-value">{finalPeakDb.toFixed(1)} dBFS</div>
                </div>
              </div>

              <div className="setting-divider">Export Options</div>

              <div className="setting-row">
                <div>
                  <div className="setting-label">Format</div>
                  <div className="setting-help">Audio file format for render</div>
                </div>
                <div className="setting-control">
                  <select
                    value={exportFormat}
                    onChange={e => setExportFormat(e.target.value as AudioFormat)}
                    aria-label="Export format"
                  >
                    <option value="wav">WAV</option>
                    <option value="aiff">AIFF</option>
                  </select>
                </div>
              </div>

              <div className="setting-row">
                <div>
                  <div className="setting-label">Bit depth</div>
                  <div className="setting-help">Sample resolution</div>
                </div>
                <div className="setting-control">
                  <select
                    value={exportBitDepth}
                    onChange={e => setExportBitDepth(Number(e.target.value) as BitDepth)}
                    aria-label="Export bit depth"
                  >
                    <option value="16">16-bit</option>
                    <option value="24">24-bit</option>
                    <option value="32">32-bit</option>
                  </select>
                </div>
              </div>

              <div className="setting-row">
                <div>
                  <div className="setting-label">Sample rate</div>
                  <div className="setting-help">Matches source file (no conversion)</div>
                </div>
                <div className="setting-control">
                  <div className="setting-value">
                    {loopState.buffer ? `${loopState.buffer.sampleRate} Hz` : '— Hz'}
                  </div>
                </div>
              </div>

              <div className="setting-divider">FX Tail Render</div>

              <div className="setting-row">
                <div>
                  <div className="setting-label">Extend render for reverb/delay tail</div>
                  <div className="setting-help">Continue rendering after loop end to capture feedback and reverb decay</div>
                </div>
                <div className="setting-control">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={extendReverbTail}
                      onChange={e => setExtendReverbTail(e.target.checked)}
                      aria-label="Extend render for reverb and delay tail"
                    />
                    <span>Enabled</span>
                  </label>
                </div>
              </div>

              {extendReverbTail && (
                <div className="setting-row">
                  <div>
                    <div className="setting-label">Tail stop threshold</div>
                    <div className="setting-help">Stop rendering when level drops below this (dBFS)</div>
                  </div>
                  <div className="setting-control">
                    <input
                      type="range"
                      min={REVERB_TAIL_THRESHOLD_MIN_DB}
                      max={REVERB_TAIL_THRESHOLD_MAX_DB}
                      step={1}
                      value={reverbTailThreshold}
                      onChange={e => setReverbTailThreshold(parseInt(e.target.value))}
                      aria-label="Reverb tail stop threshold"
                    />
                    <div className="setting-value">{reverbTailThreshold} dBFS</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isManualOpen && (
        <div className="modal-overlay" onClick={() => setIsManualOpen(false)}>
          <div className="modal modal--manual" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">User Guide</div>
              <button
                className="icon-button"
                onClick={() => setIsManualOpen(false)}
                aria-label="Close manual"
              >
                ✕
              </button>
            </div>
            <div className="modal__body manual-body">

              <section className="manual-section">
                <h3>Getting Started</h3>
                <p>Click <strong>Load Loop</strong> or drag &amp; drop audio files (WAV, AIFF, MP3, etc.) onto the sidebar or waveform area. You can load multiple files at once.</p>
                <p>Use the <strong>Sidebar</strong> to switch between loaded loops. The currently selected loop is processed by the DSP chain. Use <strong>Up/Down Arrow</strong> keys to navigate the list.</p>
                <p>Press <strong>Space</strong> to play/stop. The loop plays back continuously and resets to the start each time.</p>
              </section>

              <section className="manual-section">
                <h3>File Management</h3>
                <ul>
                  <li><strong>Load Loops</strong> &mdash; Add more files to your session at any time.</li>
                  <li><strong>Drag &amp; Drop</strong> &mdash; Drag files directly into the sidebar list.</li>
                  <li><strong>Export All</strong> &mdash; Batch renders every loaded file through the current DSP chain and downloads each one. Each file is rendered identically to a single render.</li>
                  <li><strong>Reset All</strong> &mdash; Unloads all files and resets all DSP parameters to default.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Signal Flow</h3>
                <p>Audio passes through seven serial processing modules in order:</p>
                <ol>
                  <li><strong>EQ</strong> &mdash; 4-band parametric equaliser with heat</li>
                  <li><strong>Multiband</strong> &mdash; 3-band splitter with per-band overdrive</li>
                  <li><strong>Compressor</strong> &mdash; dynamics with distortion</li>
                  <li><strong>Overdrive</strong> &mdash; full-band waveshaping</li>
                  <li><strong>Transient</strong> &mdash; transient shaper (attack / sustain)</li>
                  <li><strong>Reverb</strong> &mdash; Dattorro-style plate reverb</li>
                  <li><strong>Limiter</strong> &mdash; lookahead peak limiter</li>
                </ol>
                <p>Each module runs internally in <strong>parallel</strong> (dry + wet), but modules are chained in <strong>series</strong>. A master safety limiter sits at the very end of the chain.</p>
              </section>

              <section className="manual-section">
                <h3>Module Tabs</h3>
                <ul>
                  <li><strong>Click</strong> a tab to select that module for editing.</li>
                  <li><strong>Shift + Click</strong> a tab to toggle that module on/off.</li>
                  <li>Disabled modules are bypassed entirely (zero CPU).</li>
                  <li>All modules default to <strong>off</strong> except EQ.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Parallel Mix (right panel)</h3>
                <p>Each module has independent Dry and Wet faders with the following controls:</p>
                <ul>
                  <li><strong>S</strong> (Solo) &mdash; solos the dry or wet path independently.</li>
                  <li><strong>Gang</strong> &mdash; links both faders so they move together (same direction).</li>
                  <li><strong>Inverse</strong> &mdash; links faders inversely: raising one lowers the other. This is the default and is ideal for crossfading between dry and wet.</li>
                  <li><strong>Double-click</strong> a fader to reset it to &minus;6 dB.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Parallel / Serial Routing</h3>
                <p>Each module has a <strong>Parallel | Serial</strong> toggle:</p>
                <ul>
                  <li><strong>Parallel</strong> &mdash; dry and wet signals are mixed via the faders.</li>
                  <li><strong>Serial</strong> &mdash; dry is muted, wet is set to 0 dB. The effect is 100% in the signal path (traditional insert).</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>EQ Module</h3>
                <p>4 bands: Low Shelf, Mid 1 (peaking), Mid 2 (peaking), High Shelf. Each band has Gain, Frequency, and the mid bands have Q (bandwidth) controls. A <strong>Heat</strong> slider adds transformer-style saturation after the EQ stage. The response curve and real-time frequency analyser are displayed at the top of the module.</p>
                <p><strong>Double-click</strong> any knob to reset it to its default value.</p>
              </section>

              <section className="manual-section">
                <h3>Multiband Module</h3>
                <p>Splits the signal into 3 phase-coherent bands using Linkwitz-Riley crossovers with allpass compensation.</p>
                <ul>
                  <li><strong>Drag the top handles</strong> on the graph to set crossover frequencies.</li>
                  <li><strong>Drag the centre handles</strong> to adjust per-band gain (&plusmn;18 dB).</li>
                  <li>Each band has its own <strong>Drive</strong> (0&ndash;150%) and drive <strong>Type</strong> (tube, solid, fuzz, etc.).</li>
                  <li>A <strong>Global Drive Trim</strong> (0&ndash;200%) scales all per-band drives.</li>
                  <li>The real-time frequency analyser is overlaid on the band-split graph.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Compressor Module</h3>
                <p>Versatile dynamics processor with multiple detector types:</p>
                <ul>
                  <li><strong>Type</strong> &mdash; Peak, RMS, or DynamicsLo (upward compression / downward expansion).</li>
                  <li><strong>Controls</strong> &mdash; Threshold, Ratio, Attack, Release, Makeup Gain, Knee.</li>
                  <li><strong>Distortion</strong> &mdash; Valve (tanh) or Solid State (cubic), switchable Pre or Post compression. Amount is adjustable.</li>
                  <li>Gain reduction meter is displayed to the right of the controls.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Overdrive Module</h3>
                <p>Full-band waveshaping with multiple drive models selectable via dropdown (Tube, Solid, Fuzz, Hard Clip, Rectify, Bitcrush).</p>
                <ul>
                  <li><strong>Drive</strong> &mdash; 0&ndash;100%. Defaults to 0% (clean).</li>
                  <li><strong>Tight</strong> &mdash; pre-drive high-pass filter to tighten lows.</li>
                  <li><strong>Tone</strong> &mdash; post-drive low-pass filter to smooth highs.</li>
                  <li><strong>Bias</strong> &mdash; DC offset for asymmetric clipping character.</li>
                  <li><strong>Output</strong> &mdash; post-drive gain trim.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Transient Module</h3>
                <p>Shapes the attack and sustain of the signal using RMS change detection (Whetstone algorithm).</p>
                <ul>
                  <li><strong>Attack</strong> &mdash; boost or cut transients (&plusmn;24 dB).</li>
                  <li><strong>Sustain</strong> &mdash; boost or cut the sustained portion (&plusmn;24 dB).</li>
                  <li><strong>Hold</strong> &mdash; extends the attack envelope up to 100 ms for sustained punch.</li>
                  <li><strong>Decay</strong> &mdash; controls how quickly gain changes are applied (shared for attack and sustain).</li>
                  <li><strong>Output</strong> &mdash; output level trim.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Reverb Module</h3>
                <p>Studio-quality plate reverb based on Dattorro's figure-of-eight algorithm. The reverb uses a send/return architecture where the dry signal passes through at unity gain (0 dB) and the wet reverb signal is blended in.</p>
                <ul>
                  <li><strong>Room Type</strong> &mdash; Preset configurations: Small Room, Medium Room, Large Hall, Plate, Cathedral, Chamber.</li>
                  <li><strong>Pre-Delay</strong> &mdash; Initial delay before reverb tail (0&ndash;500 ms).</li>
                  <li><strong>Size</strong> &mdash; Overall room size (scales delay lengths).</li>
                  <li><strong>Decay</strong> &mdash; Reverb tail length (0&ndash;1).</li>
                  <li><strong>Damping</strong> &mdash; High-frequency damping in the feedback path (0 = bright, 1 = dark).</li>
                  <li><strong>Density</strong> &mdash; Controls diffusion in the reverb tank (0 = sparse, 1 = dense).</li>
                  <li><strong>Bandwidth</strong> &mdash; Input lowpass filter (0 = narrow, 1 = full bandwidth).</li>
                  <li><strong>Early/Late</strong> &mdash; Blend between early reflections and late reverb tail (0 = early only, 1 = late only).</li>
                  <li><strong>Output</strong> &mdash; Wet output level trim (&minus;60 to +6 dB).</li>
                </ul>
                <p><strong>Note:</strong> The reverb module defaults to 0 dB dry and &minus;6 dB wet for a subtle starting blend. Use the Dry/Wet faders to adjust the mix.</p>
              </section>

              <section className="manual-section">
                <h3>Limiter Module</h3>
                <p>Transparent lookahead peak limiter with 2 ms lookahead delay.</p>
                <ul>
                  <li><strong>Threshold</strong> &mdash; L2-style inverse drive (lower = more limiting).</li>
                  <li><strong>Ceiling</strong> &mdash; maximum output level.</li>
                  <li><strong>Release</strong> &mdash; gain recovery time.</li>
                  <li><strong>Knee</strong> &mdash; soft knee width.</li>
                  <li><strong>Mode</strong> &mdash; Pumping (fast), Normal, or Smooth (slow release).</li>
                  <li>Gain reduction meter is displayed to the right of the controls.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Rendering &amp; Export</h3>
                <p>Press <strong>R</strong> or click <strong>Render (R)</strong> to render the currently selected loop through the entire DSP chain and download it.</p>
                <ul>
                  <li>The render uses the <strong>exact same processing</strong> as real-time playback &mdash; every module, every parameter, every mix setting.</li>
                  <li>Output is <strong>sample-accurate</strong>: same length, same sample rate, perfectly aligned with the original loop.</li>
                  <li>A warm-render strategy plays the loop through the processors twice before capturing the output, ensuring compressors, limiters, and filters are fully settled (no startup transients).</li>
                  <li>Output format (WAV/AIFF) and bit depth (16/24/32) are configured in <strong>Settings</strong> (gear icon).</li>
                  <li><strong>Extend render for reverb tail</strong> (Settings) &mdash; When enabled, continues rendering after the loop ends to capture the reverb decay. Stops automatically when the output level drops below the threshold (default &minus;75 dBFS). Useful for capturing long reverb tails that extend beyond the loop length.</li>
                </ul>
                <p><strong>Export All</strong> renders every loaded file individually through the processing chain. Each file gets its own fresh render &mdash; identical to pressing R for each one. Progress is shown in the status bar.</p>
              </section>

              <section className="manual-section">
                <h3>Keyboard Shortcuts</h3>
                <ul>
                  <li><strong>Space</strong> &mdash; Play / Stop (resets to start).</li>
                  <li><strong>R</strong> &mdash; Render and download the processed loop.</li>
                  <li><strong>Up / Down Arrow</strong> &mdash; Navigate between loaded files.</li>
                </ul>
              </section>

              <section className="manual-section">
                <h3>Tips</h3>
                <ul>
                  <li>Start with subtle settings and A/B using the Dry/Wet faders.</li>
                  <li>Use <strong>Inverse</strong> mode (default) for natural crossfading between dry and wet.</li>
                  <li>Solo the Wet path to hear just the effect in isolation.</li>
                  <li>Watch the VU meters at the bottom to keep levels healthy.</li>
                  <li>The master safety limiter protects your speakers &mdash; adjust its threshold in Settings.</li>
                </ul>
              </section>

            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default App
